import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ParticularService from '../services/ParticularService';
import particularesApiService from '../services/ParticularesApiService';
import UserSessionMenu from './common/UserSessionMenu';
import Alert from './common/Alert';
import SearchableSelect from './common/SearchableSelect';
import LoadingSpinner from './common/LoadingSpinner';
import TableExportActions from './common/TableExportActions';
import AulaRecursosModal from './common/AulaRecursosModal';
import AulaStatsModal from './common/AulaStatsModal';
import SubscriptionBlockedModal from './common/SubscriptionBlockedModal';
import ParticularDisponibilidadCalendar from './ParticularDisponibilidadCalendar';
import ParticularDashboardMenu, { buildParticularDashboardMenuItems } from './common/menus/ParticularDashboardMenu';
import { ITEMS_PER_PAGE_OPTIONS } from '../utils/pagination';
import { isAdminColegio } from '../utils/adminMenu';
import {
  CALENDARIO_SUBSCRIPTION_BLOCKED_MESSAGE,
  isParticularCalendarioAllowed,
  isParticularCalendarioMenuItem,
  isParticularChatGPTAllowed,
  isParticularChatGPTMenuItem
} from '../utils/particularChatGPTAccess';
import brandLogo from '../assets/logos/logo_transparent_background.png';
import './NewDashboard.css';
import './AdminDashboard.css';
import './ParticularDashboard.css';
import './ParticularProTheme.css';

const emptyEtiquetaForm = { id: '', nombre: '', descripcion: '', color: '#2563eb' };
const emptyProfesorAsociadoForm = { id: '', email: '', nombre: '', apellido: '', telefono: '', dni: '', activo: true };
const emptyAlumnoForm = { id: '', email: '', nombre: '', apellido: '', telefono: '', dni: '', activo: true };
const emptyInvitacionForm = { id: '', codigo: '', fechaCaducidad: '', activo: true };
const emptyCuentaCorrienteForm = {
  id: '',
  tipoMovimiento: 'Debito',
  tipoEntidad: 'Alumno',
  idUsuarioEntidad: '',
  concepto: '',
  notas: '',
  importe: '',
  fechaMovimiento: ''
};
const emptyCursoForm = { id: '', nombre: '', descripcion: '', etiquetaIds: [], profesorIds: [], alumnoIds: [] };
const emptyAulaForm = {
  id: '',
  idCursoParticular: '',
  nombre: '',
  descripcion: '',
  aulaPublica: false,
  mostrarArchivos: true,
  mostrarLinks: true,
  mostrarPizarra: true,
  mostrarChat: true,
  mostrarVideollamada: true,
  mostrarForo: true,
  mostrarAsistentesIA: true
};

const getId = (item) => String(item?.id || item?.Id || '');
const getName = (item, fallback = 'Sin nombre') => item?.nombre || item?.Nombre || item?.displayName || item?.DisplayName || fallback;
const getEmail = (item) => item?.email || item?.Email || '';
const getDni = (item) => String(item?.dni || item?.Dni || '').trim();
const getDescripcion = (item) => item?.descripcion || item?.Descripcion || '';
const getSurname = (item) => item?.apellido || item?.Apellido || '';
const getFullName = (item, fallback = 'Sin nombre') => {
  const fullName = [item?.nombre || item?.Nombre || item?.displayName || item?.DisplayName || '', getSurname(item)]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fullName || getEmail(item) || fallback;
};
const getList = (response, lowerKey, upperKey) => {
  const value = response?.[lowerKey] || response?.[upperKey] || [];
  return Array.isArray(value) ? value : [];
};
const getBool = (item, lowerKey, upperKey, fallback = false) => item?.[lowerKey] ?? item?.[upperKey] ?? fallback;
const normalizeIds = (items = []) => items.map(getId).filter(Boolean);
const normalizeConfirmText = (value) => String(value || '').trim().replace(/\s+/g, ' ').toUpperCase();
const getAssistantId = (item) => item?.idOpenAIAssistant || item?.IdOpenAIAssistant || getId(item);
const getAssistantName = (item) => (
  item?.gptClasicoNombre ||
  item?.GPTClasicoNombre ||
  item?.nombre ||
  item?.Nombre ||
  'Asistente IA'
);
const getAssistantUrl = (item) => (
  item?.gptClasicoURLPublica ||
  item?.GPTClasicoURLPublica ||
  item?.gptClasicoUrlPublica ||
  item?.GptClasicoUrlPublica ||
  ''
);
const getAssistantDescription = (item) => (
  item?.gptClasicoDescripcion ||
  item?.GPTClasicoDescripcion ||
  item?.descripcion ||
  item?.Descripcion ||
  ''
);

const DEFAULT_GRID_PAGE_SIZE = 50;
const INVITATION_SHARE_CARD_WIDTH = 1400;
const INVITATION_SHARE_CARD_HEIGHT = 900;
const PAGE_SIZE_OPTIONS = ITEMS_PER_PAGE_OPTIONS.map((option) => ({
  value: String(option),
  label: `${option}`
}));
const dashboardNumberFormatter = new Intl.NumberFormat('es-AR');
const currencyFormatter = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' });
const formatDashboardNumber = (value, fallback = 0) => {
  const number = Number(value);
  return dashboardNumberFormatter.format(Number.isFinite(number) ? number : fallback);
};
const formatMetricWithLabel = (value, singular, plural = `${singular}s`) => {
  const amount = Number(value) || 0;
  return `${formatDashboardNumber(amount)} ${amount === 1 ? singular : plural}`;
};
const formatCurrency = (value) => {
  const number = Number(value);
  return currencyFormatter.format(Number.isFinite(number) ? number : 0);
};
const groupAmountInteger = (digits) => String(digits || '')
  .replace(/\D/g, '')
  .replace(/^0+(?=\d)/, '')
  .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const formatCuentaCorrienteAmountInput = (value) => {
  const raw = String(value ?? '');
  if (!raw.trim()) return '';

  const lastCommaIndex = raw.lastIndexOf(',');
  const lastDotIndex = raw.lastIndexOf('.');
  const dotDecimalDigits = lastDotIndex >= 0 ? raw.slice(lastDotIndex + 1).replace(/\D/g, '') : '';
  const useDotAsDecimal = lastCommaIndex < 0 &&
    lastDotIndex >= 0 &&
    raw.indexOf('.') === lastDotIndex &&
    (raw.endsWith('.') || (dotDecimalDigits.length > 0 && dotDecimalDigits.length <= 2));
  const decimalSeparatorIndex = lastCommaIndex >= 0 ? lastCommaIndex : useDotAsDecimal ? lastDotIndex : -1;
  const hasDecimalSeparator = decimalSeparatorIndex >= 0;
  const integerRaw = hasDecimalSeparator ? raw.slice(0, decimalSeparatorIndex) : raw;
  const decimalRaw = hasDecimalSeparator ? raw.slice(decimalSeparatorIndex + 1) : '';
  const integerText = groupAmountInteger(integerRaw) || (hasDecimalSeparator ? '0' : '');
  const decimalText = decimalRaw.replace(/\D/g, '').slice(0, 2);

  if (!integerText && !hasDecimalSeparator) return '';
  return hasDecimalSeparator ? `${integerText},${decimalText}` : integerText;
};
const parseCuentaCorrienteAmountInput = (value) => {
  const formatted = formatCuentaCorrienteAmountInput(value);
  if (!formatted) return 0;
  const normalized = formatted.replace(/\./g, '').replace(',', '.');
  const number = Number(normalized);
  return Number.isFinite(number) ? number : 0;
};
const formatCuentaCorrienteAmountFromNumber = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return '';
  return number.toLocaleString('es-AR', {
    minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
    maximumFractionDigits: 2
  });
};
const formatDashboardDecimal = (value, digits = 1) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString('es-AR', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : '0';
};
const formatStorageGigabytes = (bytes) => {
  const number = Number(bytes);
  if (!Number.isFinite(number) || number <= 0) return '0,00 GB';
  return `${(number / (1024 ** 3)).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GB`;
};
const formatDashboardDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};
const getDashboardProp = (source, ...keys) => {
  for (const key of keys) {
    if (source && source[key] !== undefined && source[key] !== null) return source[key];
  }
  return undefined;
};
const getDashboardArray = (source, ...keys) => {
  const value = getDashboardProp(source, ...keys);
  return Array.isArray(value) ? value : [];
};

const DASHBOARD_EVENT_BREAKDOWN = [
  { key: 'visitas', pascalKey: 'Visitas', label: 'Aulas' },
  { key: 'chats', pascalKey: 'Chats', label: 'Chat' },
  { key: 'asistentes', pascalKey: 'Asistentes', label: 'IA' },
  { key: 'pizarra', pascalKey: 'Pizarra', label: 'Pizarra' },
  { key: 'videollamadas', pascalKey: 'Videollamadas', label: 'Video' },
  { key: 'foro', pascalKey: 'Foro', label: 'Tablón' },
  { key: 'descargas', pascalKey: 'Descargas', label: 'Archivos' }
];

const getLineChartScale = (maxValue) => {
  const rawMax = Math.max(1, Number(maxValue) || 0);
  if (rawMax <= 4) return { axisMax: 4, tickStep: 1 };
  if (rawMax <= 8) return { axisMax: Math.ceil(rawMax / 2) * 2, tickStep: 2 };

  const roughStep = rawMax / 4;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;
  const niceStep = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const tickStep = niceStep * magnitude;

  return {
    axisMax: Math.ceil(rawMax / tickStep) * tickStep,
    tickStep
  };
};

const getLineChartTicks = (axisMax, tickStep) => {
  const ticks = [];
  for (let tick = 0; tick <= axisMax; tick += tickStep) {
    ticks.push(tick);
  }
  if (ticks[ticks.length - 1] !== axisMax) ticks.push(axisMax);
  return ticks;
};

const gridKeys = ['resumenCursos', 'etiquetas', 'profesoresAsociados', 'alumnos', 'invitaciones', 'cursos', 'aulas', 'asistentes'];
const createGridState = () => ({ search: '', page: 1, pageSize: DEFAULT_GRID_PAGE_SIZE });
const createInitialGridState = () => (
  gridKeys.reduce((state, key) => ({ ...state, [key]: createGridState() }), {})
);
const createCuentaCorrienteFilters = () => ({
  search: '',
  fechaDesde: '',
  fechaHasta: '',
  tipoMovimiento: 'todos',
  tipoEntidad: 'todos',
  idUsuarioEntidad: '',
  page: 1,
  pageSize: DEFAULT_GRID_PAGE_SIZE
});
const createCuentaCorrienteResumen = () => ({
  ingresos: 0,
  salidas: 0,
  saldo: 0,
  ingresosMes: 0,
  salidasMes: 0,
  saldoMes: 0,
  totalMovimientos: 0
});

const normalizeSearchText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const isSubscriptionLimitError = (error) => {
  const message = normalizeSearchText(error?.message);
  return message.includes('suscripcion') && (message.includes('limite') || message.includes('no podes crear mas'));
};

const getArray = (value) => (Array.isArray(value) ? value : []);
const joinSearchValues = (values) => values
  .flatMap((value) => (Array.isArray(value) ? value : [value]))
  .filter((value) => value !== undefined && value !== null && value !== '')
  .join(' ');
const getListNames = (items, formatter = getName) => getArray(items)
  .map((item) => formatter(item))
  .filter(Boolean)
  .join(', ') || '-';
const getEstadoText = (active) => (active ? 'Activo' : 'Inactivo');
const getInvitationCode = (invitacion) => invitacion?.codigo || invitacion?.Codigo || '';
const getInvitationStatus = (invitacion) => invitacion?.estado || invitacion?.Estado || (getBool(invitacion, 'activo', 'Activo', true) ? 'Activa' : 'Inactiva');
const getInvitationCount = (invitacion) => invitacion?.cantidadRegistros ?? invitacion?.CantidadRegistros ?? 0;
const getInvitationExpiration = (invitacion) => invitacion?.fechaCaducidadUtc || invitacion?.FechaCaducidadUtc || '';
const getInvitationRegistrations = (invitacion) => getArray(invitacion?.registros || invitacion?.Registros);
const formatDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toISOString().slice(0, 10);
};
const formatDateOnly = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const getTomorrowDateInput = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
};
const getTodayDateInput = () => new Date().toISOString().slice(0, 10);
const buildRandomInvitationCode = () => {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint32Array(6);
  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * alphabet.length);
  }
  return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('');
};
const escapeInvitationSvg = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
const wrapInvitationSvgText = (text, maxCharsPerLine = 30, maxLines = 2) => {
  const words = String(text || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .flatMap((word) => {
      if (word.length <= maxCharsPerLine) return [word];
      const chunks = [];
      for (let index = 0; index < word.length; index += maxCharsPerLine) {
        chunks.push(word.slice(index, index + maxCharsPerLine));
      }
      return chunks;
    });

  if (!words.length) return [''];

  const lines = [];
  let currentLine = '';
  words.forEach((word) => {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (candidate.length <= maxCharsPerLine || !currentLine) {
      currentLine = candidate;
      return;
    }
    lines.push(currentLine);
    currentLine = word;
  });
  if (currentLine) lines.push(currentLine);

  if (lines.length <= maxLines) return lines;

  const visibleLines = lines.slice(0, maxLines);
  visibleLines[maxLines - 1] = `${visibleLines[maxLines - 1]} ${lines.slice(maxLines).join(' ')}`.trim();
  if (visibleLines[maxLines - 1].length > maxCharsPerLine) {
    visibleLines[maxLines - 1] = `${visibleLines[maxLines - 1].slice(0, maxCharsPerLine - 3).trimEnd()}...`;
  }
  return visibleLines;
};
const renderInvitationSvgTextLines = (lines, x, y, lineHeight, extraAttributes = '') => `
  <text x="${x}" y="${y}" ${extraAttributes}>
    ${lines
      .map((line, index) => `<tspan x="${x}"${index === 0 ? '' : ` dy="${lineHeight}"`}>${escapeInvitationSvg(line)}</tspan>`)
      .join('')}
  </text>
`;
const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('No pudimos leer el recurso generado.'));
    reader.readAsDataURL(blob);
  });
const fetchAssetAsDataUrl = async (assetUrl) => {
  const response = await fetch(assetUrl);
  if (!response.ok) {
    throw new Error('No pudimos preparar los recursos para compartir.');
  }
  return blobToDataUrl(await response.blob());
};
const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('No pudimos renderizar la imagen para compartir.'));
    image.src = src;
  });
const downloadBlob = (blob, fileName) => {
  const downloadUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = downloadUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);
};
const sanitizeInvitationFileName = (value = 'invitacion') =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'invitacion';
const buildInvitationShareFileName = (particularName, code) =>
  `aluxia-invitacion-${sanitizeInvitationFileName(particularName || 'particular')}-${sanitizeInvitationFileName(code || 'codigo')}.png`;
const getPublicAppOrigin = () => {
  const configuredUrl = process.env.REACT_APP_PUBLIC_APP_URL || '';
  if (configuredUrl) {
    try {
      return new URL(configuredUrl).origin;
    } catch {
      return configuredUrl.replace(/\/+$/, '');
    }
  }
  return window.location.origin;
};
const buildInvitationShareCardSvg = ({ particularName, code, qrDataUrl, logoDataUrl }) => {
  const nameLines = wrapInvitationSvgText(particularName || 'Cuenta particular', 24, 2);

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${INVITATION_SHARE_CARD_WIDTH}" height="${INVITATION_SHARE_CARD_HEIGHT}" viewBox="0 0 ${INVITATION_SHARE_CARD_WIDTH} ${INVITATION_SHARE_CARD_HEIGHT}">
      <defs>
        <linearGradient id="invitation-bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#102a43" />
          <stop offset="55%" stop-color="#2563eb" />
          <stop offset="100%" stop-color="#0f766e" />
        </linearGradient>
        <linearGradient id="invitation-panel" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#0f172a" />
          <stop offset="100%" stop-color="#111827" />
        </linearGradient>
        <filter id="invitation-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="18" stdDeviation="26" flood-color="#0f172a" flood-opacity="0.22" />
        </filter>
      </defs>

      <rect width="${INVITATION_SHARE_CARD_WIDTH}" height="${INVITATION_SHARE_CARD_HEIGHT}" fill="url(#invitation-bg)" />
      <circle cx="1240" cy="96" r="178" fill="#ffffff" fill-opacity="0.08" />
      <circle cx="140" cy="804" r="220" fill="#38bdf8" fill-opacity="0.16" />
      <circle cx="926" cy="790" r="126" fill="#22c55e" fill-opacity="0.12" />

      <rect x="80" y="84" width="1240" height="732" rx="36" fill="#ffffff" fill-opacity="0.12" />
      <rect x="100" y="104" width="1200" height="692" rx="34" fill="#ffffff" filter="url(#invitation-shadow)" />
      <rect x="120" y="124" width="700" height="652" rx="30" fill="url(#invitation-panel)" />
      <rect x="854" y="146" width="366" height="608" rx="30" fill="#f8fafc" filter="url(#invitation-shadow)" />

      <rect x="154" y="154" width="110" height="110" rx="28" fill="#ffffff" fill-opacity="0.14" />
      ${logoDataUrl ? `<image href="${logoDataUrl}" x="176" y="176" width="66" height="66" preserveAspectRatio="xMidYMid meet" />` : ''}

      <text x="290" y="195" font-family="Arial, Helvetica, sans-serif" font-size="22" font-weight="700" fill="#bfdbfe">aluxIA</text>
      <text x="290" y="232" font-family="Arial, Helvetica, sans-serif" font-size="18" font-weight="500" fill="#cbd5e1">Invitacion para alumnos particulares</text>

      <rect x="154" y="304" width="180" height="42" rx="21" fill="#ffffff" fill-opacity="0.12" stroke="#ffffff" stroke-opacity="0.22" />
      <text x="244" y="331" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="16" font-weight="600" fill="#f8fafc">Nuevo acceso</text>

      <text x="154" y="402" font-family="Arial, Helvetica, sans-serif" font-size="54" font-weight="700" fill="#ffffff">Sumate a tu</text>
      <text x="154" y="464" font-family="Arial, Helvetica, sans-serif" font-size="54" font-weight="700" fill="#ffffff">aula particular</text>

      ${renderInvitationSvgTextLines(
        nameLines,
        154,
        552,
        42,
        'font-family="Arial, Helvetica, sans-serif" font-size="32" font-weight="700" fill="#bfdbfe"'
      )}

      <text x="154" y="662" font-family="Arial, Helvetica, sans-serif" font-size="22" font-weight="600" fill="#e2e8f0">Escanea el QR o ingresa el codigo de invitacion.</text>
      <text x="154" y="704" font-family="Arial, Helvetica, sans-serif" font-size="22" font-weight="600" fill="#e2e8f0">Primer acceso: tu contrasena sera tu DNI.</text>

      <rect x="906" y="196" width="262" height="262" rx="28" fill="#ffffff" />
      ${qrDataUrl ? `<image href="${qrDataUrl}" x="930" y="220" width="214" height="214" preserveAspectRatio="xMidYMid meet" />` : ''}
      <text x="1037" y="492" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="18" font-weight="700" fill="#0f172a">Escaneame</text>
      <text x="1037" y="520" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="16" font-weight="500" fill="#475569">Alta de alumno particular</text>

      <text x="1037" y="592" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="16" font-weight="800" fill="#64748b">CODIGO</text>
      <text x="1037" y="648" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="46" font-weight="800" fill="#2563eb" letter-spacing="0">${escapeInvitationSvg(code || '-')}</text>
      <text x="1037" y="704" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="15" font-weight="700" fill="#64748b">Ingresa el codigo si no podes escanear el QR</text>
    </svg>
  `;
};
const getInvitationSearchText = (invitacion) => joinSearchValues([
  getInvitationCode(invitacion),
  getInvitationStatus(invitacion),
  formatDateOnly(getInvitationExpiration(invitacion)),
  getInvitationCount(invitacion)
]);
const getMovimientoTipo = (movimiento) => movimiento?.tipoMovimiento || movimiento?.TipoMovimiento || '';
const getMovimientoEntidadTipo = (movimiento) => movimiento?.tipoEntidad || movimiento?.TipoEntidad || '';
const getMovimientoEntidad = (movimiento) => movimiento?.entidad || movimiento?.Entidad || {};
const getMovimientoConcepto = (movimiento) => movimiento?.concepto || movimiento?.Concepto || '';
const getMovimientoNotas = (movimiento) => movimiento?.notas || movimiento?.Notas || '';
const getMovimientoImporte = (movimiento) => Number(movimiento?.importe ?? movimiento?.Importe ?? 0) || 0;
const getMovimientoImporteFirmado = (movimiento) => Number(movimiento?.importeFirmado ?? movimiento?.ImporteFirmado ?? 0) || 0;
const getMovimientoFecha = (movimiento) => movimiento?.fechaMovimiento || movimiento?.FechaMovimiento || '';

const getCursoSearchText = (curso) => joinSearchValues([
  getName(curso, ''),
  getDescripcion(curso),
  getArray(curso?.etiquetas || curso?.Etiquetas).map((etiqueta) => getName(etiqueta, '')),
  getArray(curso?.profesores || curso?.Profesores).map((profesor) => joinSearchValues([getFullName(profesor, ''), getEmail(profesor)])),
  getArray(curso?.alumnos || curso?.Alumnos).map((alumno) => joinSearchValues([getFullName(alumno, ''), getEmail(alumno), alumno?.dni || alumno?.Dni])),
  curso?.cantidadAulas ?? curso?.CantidadAulas ?? ''
]);

const getEtiquetaSearchText = (etiqueta) => joinSearchValues([
  getName(etiqueta, ''),
  getDescripcion(etiqueta),
  etiqueta?.color || etiqueta?.Color || '',
  getBool(etiqueta, 'activo', 'Activo', true) ? 'activo' : 'inactivo'
]);

const getAlumnoSearchText = (alumno) => joinSearchValues([
  getFullName(alumno, ''),
  getEmail(alumno),
  alumno?.dni || alumno?.Dni || '',
  alumno?.telefono || alumno?.Telefono || '',
  getBool(alumno, 'activo', 'Activo', true) ? 'activo' : 'inactivo'
]);

const getProfesorAsociadoSearchText = (profesorAsociado) => joinSearchValues([
  getFullName(profesorAsociado, ''),
  getEmail(profesorAsociado),
  profesorAsociado?.dni || profesorAsociado?.Dni || '',
  profesorAsociado?.telefono || profesorAsociado?.Telefono || '',
  getBool(profesorAsociado, 'activo', 'Activo', true) ? 'activo' : 'inactivo'
]);

const getAulaSearchText = (aula) => joinSearchValues([
  getName(aula, ''),
  getDescripcion(aula),
  aula?.cursoNombre || aula?.CursoNombre || '',
  aula?.nicknameUrl || aula?.NicknameUrl || '',
  aula?.cantidadAsistentes ?? aula?.CantidadAsistentes ?? ''
]);

const getAssistantSearchText = (asistente) => joinSearchValues([
  getAssistantName(asistente),
  getAssistantDescription(asistente),
  getBool(asistente, 'gptClasicoCreacionFinalizada', 'GPTClasicoCreacionFinalizada', false) ? 'listo' : 'pendiente',
  getAssistantUrl(asistente)
]);

const cursoExportColumns = [
  { key: 'nombre', title: 'Curso', exportValue: (_, curso) => getName(curso) },
  { key: 'descripcion', title: 'Descripción', exportValue: (_, curso) => getDescripcion(curso) || '-' },
  { key: 'etiquetas', title: 'Etiquetas', exportValue: (_, curso) => getListNames(curso?.etiquetas || curso?.Etiquetas) },
  { key: 'profesores', title: 'Profesores', exportValue: (_, curso) => getListNames(curso?.profesores || curso?.Profesores, getFullName) },
  { key: 'alumnos', title: 'Alumnos', exportValue: (_, curso) => getListNames(curso?.alumnos || curso?.Alumnos, getFullName) },
  { key: 'cantidadAulas', title: 'Aulas', exportValue: (_, curso) => curso?.cantidadAulas ?? curso?.CantidadAulas ?? 0 }
];

const etiquetaExportColumns = [
  { key: 'color', title: 'Color', exportValue: (_, etiqueta) => etiqueta?.color || etiqueta?.Color || '-' },
  { key: 'nombre', title: 'Nombre', exportValue: (_, etiqueta) => getName(etiqueta) },
  { key: 'descripcion', title: 'Descripción', exportValue: (_, etiqueta) => getDescripcion(etiqueta) || '-' },
  { key: 'activo', title: 'Estado', exportValue: (_, etiqueta) => getEstadoText(getBool(etiqueta, 'activo', 'Activo', true)) }
];

const profesorExportColumns = [
  { key: 'profesor', title: 'Profesor', exportValue: (_, profesor) => getFullName(profesor, getEmail(profesor)) },
  { key: 'email', title: 'Email', exportValue: (_, profesor) => getEmail(profesor) || '-' },
  { key: 'dni', title: 'DNI', exportValue: (_, profesor) => profesor?.dni || profesor?.Dni || '-' },
  { key: 'telefono', title: 'Teléfono', exportValue: (_, profesor) => profesor?.telefono || profesor?.Telefono || '-' },
  { key: 'activo', title: 'Estado', exportValue: (_, profesor) => getEstadoText(getBool(profesor, 'activo', 'Activo', true)) }
];

const alumnoExportColumns = [
  { key: 'alumno', title: 'Alumno', exportValue: (_, alumno) => getFullName(alumno, getEmail(alumno)) },
  { key: 'email', title: 'Email', exportValue: (_, alumno) => getEmail(alumno) || '-' },
  { key: 'dni', title: 'DNI', exportValue: (_, alumno) => alumno?.dni || alumno?.Dni || '-' },
  { key: 'telefono', title: 'Teléfono', exportValue: (_, alumno) => alumno?.telefono || alumno?.Telefono || '-' },
  { key: 'activo', title: 'Estado', exportValue: (_, alumno) => getEstadoText(getBool(alumno, 'activo', 'Activo', true)) }
];

const invitacionExportColumns = [
  { key: 'codigo', title: 'Código', exportValue: (_, invitacion) => getInvitationCode(invitacion) },
  { key: 'fechaCaducidad', title: 'Caducidad', exportValue: (_, invitacion) => formatDateOnly(getInvitationExpiration(invitacion)) },
  { key: 'estado', title: 'Estado', exportValue: (_, invitacion) => getInvitationStatus(invitacion) },
  { key: 'registros', title: 'Registrados', exportValue: (_, invitacion) => getInvitationCount(invitacion) }
];

const cuentaCorrienteExportColumns = [
  { key: 'fecha', title: 'Fecha', exportValue: (_, movimiento) => formatDateOnly(getMovimientoFecha(movimiento)) },
  { key: 'tipo', title: 'Tipo', exportValue: (_, movimiento) => getMovimientoTipo(movimiento) },
  { key: 'entidadTipo', title: 'Entidad', exportValue: (_, movimiento) => getMovimientoEntidadTipo(movimiento) },
  { key: 'entidad', title: 'Nombre', exportValue: (_, movimiento) => getFullName(getMovimientoEntidad(movimiento), 'Empresa') },
  { key: 'concepto', title: 'Concepto', exportValue: (_, movimiento) => getMovimientoConcepto(movimiento) },
  { key: 'importe', title: 'Importe', exportValue: (_, movimiento) => getMovimientoImporteFirmado(movimiento) },
  { key: 'notas', title: 'Notas', exportValue: (_, movimiento) => getMovimientoNotas(movimiento) || '-' }
];

const aulaExportColumns = [
  { key: 'nombre', title: 'Aula', exportValue: (_, aula) => getName(aula) },
  { key: 'descripcion', title: 'Descripción', exportValue: (_, aula) => getDescripcion(aula) || '-' },
  { key: 'curso', title: 'Curso', exportValue: (_, aula) => aula?.cursoNombre || aula?.CursoNombre || '-' },
  { key: 'nicknameUrl', title: 'Nickname', exportValue: (_, aula) => aula?.nicknameUrl || aula?.NicknameUrl || '-' },
  { key: 'cantidadAsistentes', title: 'Asistentes', exportValue: (_, aula) => aula?.cantidadAsistentes ?? aula?.CantidadAsistentes ?? 0 }
];

const asistenteExportColumns = [
  { key: 'asistente', title: 'Asistente', exportValue: (_, asistente) => getAssistantName(asistente) },
  { key: 'descripcion', title: 'Descripción', exportValue: (_, asistente) => getAssistantDescription(asistente) || '-' },
  { key: 'estado', title: 'Estado', exportValue: (_, asistente) => (getBool(asistente, 'gptClasicoCreacionFinalizada', 'GPTClasicoCreacionFinalizada', false) ? 'Listo' : 'Pendiente') }
];

const getPagedGrid = (items, state, getSearchText) => {
  const source = Array.isArray(items) ? items : [];
  const safeState = { ...createGridState(), ...(state || {}) };
  const pageSize = Number(safeState.pageSize) || DEFAULT_GRID_PAGE_SIZE;
  const search = normalizeSearchText(safeState.search);
  const filteredItems = search
    ? source.filter((item) => normalizeSearchText(getSearchText(item)).includes(search))
    : source;
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const currentPage = Math.min(Math.max(Number(safeState.page) || 1, 1), totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const pageItems = filteredItems.slice(startIndex, startIndex + pageSize);

  return {
    items: pageItems,
    filteredItems,
    totalItems: source.length,
    filteredTotal: filteredItems.length,
    pageSize,
    currentPage,
    totalPages,
    startItem: filteredItems.length ? startIndex + 1 : 0,
    endItem: filteredItems.length ? startIndex + pageItems.length : 0
  };
};

const ToggleField = ({ checked, label, onChange }) => (
  <label className="particular-toggle">
    <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    <span>{label}</span>
  </label>
);

const TagPill = ({ etiqueta }) => (
  <span className="particular-tag" style={{ '--tag-color': etiqueta?.color || etiqueta?.Color || '#2563eb' }}>
    {getName(etiqueta, 'Etiqueta')}
  </span>
);

const StatusPill = ({ active = true, trueText = 'Activo', falseText = 'Inactivo' }) => (
  <span className={`particular-status ${active ? 'ok' : ''}`}>
    {active ? trueText : falseText}
  </span>
);

const SectionHeader = ({ icon, title, subtitle, onNew, newLabel = 'Nuevo', children }) => (
  <div className="card-header particular-card-header">
    <div>
      <h2 className="card-title">
        <i className={icon} />
        {title}
      </h2>
      {subtitle && <p className="particular-card-subtitle">{subtitle}</p>}
    </div>
    <div className="particular-toolbar-actions">
      {children}
      {onNew && (
        <button type="button" className="particular-primary-btn" onClick={onNew}>
          <i className="fas fa-plus" />
          {newLabel}
        </button>
      )}
    </div>
  </div>
);

const Modal = ({ title, icon, children, onClose, className = '' }) => (
  <div className="particular-modal-overlay" onMouseDown={onClose}>
    <div className={`particular-modal ${className}`.trim()} onMouseDown={(event) => event.stopPropagation()}>
      <div className="particular-modal-header">
        <h3>
          <i className={icon} />
          {title}
        </h3>
        <button type="button" onClick={onClose} title="Cerrar">
          <i className="fas fa-times" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

const TableEmpty = ({ colSpan, text }) => (
  <tr>
    <td colSpan={colSpan}>
      <div className="particular-empty-inline">{text}</div>
    </td>
  </tr>
);

const CourseCountButton = ({ value, title, onClick }) => (
  <button type="button" className="particular-count-trigger" onClick={onClick} title={title}>
    {value}
  </button>
);

const GridToolbar = ({
  state,
  grid,
  placeholder,
  onSearchChange,
  onPageSizeChange,
  exportColumns = [],
  exportRows = [],
  exportFileName = 'grilla',
  exportTitle = 'Exportación de grilla'
}) => (
  <div className="particular-grid-toolbar">
    <label className="particular-grid-search">
      <span>Buscar</span>
      <div className="particular-grid-search-box">
        <i className="fas fa-search" />
        <input
          value={state.search || ''}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={placeholder}
        />
      </div>
    </label>
    <div className="particular-grid-meta">
      <span>
        {grid.filteredTotal === grid.totalItems
          ? `${grid.totalItems} registros`
          : `${grid.filteredTotal} de ${grid.totalItems} registros`}
      </span>
      <div className="particular-page-size">
        <span>Mostrar</span>
        <SearchableSelect
          value={String(state.pageSize || DEFAULT_GRID_PAGE_SIZE)}
          onChange={(value) => onPageSizeChange(Number(value) || DEFAULT_GRID_PAGE_SIZE)}
          options={PAGE_SIZE_OPTIONS}
          placeholder={`${DEFAULT_GRID_PAGE_SIZE}`}
        />
      </div>
      <TableExportActions
        columns={exportColumns}
        rows={exportRows}
        fileName={exportFileName}
        title={exportTitle}
        compact
        disabled={!exportRows.length}
      />
    </div>
  </div>
);

const GridPagination = ({ grid, onPageChange }) => {
  if (grid.filteredTotal === 0) return null;

  return (
    <div className="particular-pagination">
      <span>
        Mostrando {grid.startItem}-{grid.endItem} de {grid.filteredTotal}
      </span>
      <div className="particular-pagination-actions">
        <button
          type="button"
          className="particular-icon-btn"
          onClick={() => onPageChange(grid.currentPage - 1)}
          disabled={grid.currentPage <= 1}
          title="Página anterior"
        >
          <i className="fas fa-chevron-left" />
        </button>
        <strong>Página {grid.currentPage} de {grid.totalPages}</strong>
        <button
          type="button"
          className="particular-icon-btn"
          onClick={() => onPageChange(grid.currentPage + 1)}
          disabled={grid.currentPage >= grid.totalPages}
          title="Página siguiente"
        >
          <i className="fas fa-chevron-right" />
        </button>
      </div>
    </div>
  );
};

const GridLoadingOverlay = () => (
  <div className="particular-grid-loading-overlay">
    <LoadingSpinner message="Actualizando grilla..." size="small" inline />
  </div>
);

const SubmitButton = ({ loading, disabled, children = 'Guardar', loadingText = 'Guardando...' }) => (
  <button type="submit" className="particular-primary-btn" disabled={loading || disabled}>
    {loading && <i className="fas fa-spinner fa-spin" />}
    {loading ? loadingText : children}
  </button>
);

const DashboardIllustration = () => (
  <svg className="particular-dashboard-illustration" viewBox="0 0 240 160" role="img" aria-label="Panel de indicadores">
    <rect x="22" y="24" width="196" height="112" rx="10" />
    <path d="M42 105 L76 83 L112 92 L148 58 L198 72" />
    <circle cx="42" cy="105" r="5" />
    <circle cx="76" cy="83" r="5" />
    <circle cx="112" cy="92" r="5" />
    <circle cx="148" cy="58" r="5" />
    <circle cx="198" cy="72" r="5" />
    <rect x="46" y="118" width="22" height="10" rx="3" />
    <rect x="78" y="111" width="22" height="17" rx="3" />
    <rect x="110" y="101" width="22" height="27" rx="3" />
    <rect x="142" y="90" width="22" height="38" rx="3" />
    <rect x="174" y="80" width="22" height="48" rx="3" />
    <path d="M50 46 h68 M50 61 h42" className="muted" />
  </svg>
);

const DashboardKpiCard = ({ icon, label, value, displayValue, detail, tone = 'blue', className = '' }) => (
  <article className={`particular-kpi-card tone-${tone} ${className}`.trim()}>
    <div className="particular-kpi-icon">
      <i className={icon} />
    </div>
    <div>
      <span>{label}</span>
      <strong>{displayValue ?? formatDashboardNumber(value)}</strong>
      {detail && <small>{detail}</small>}
    </div>
  </article>
);

const ChartCard = ({ icon, title, subtitle, children, className = '' }) => (
  <section className={`particular-dashboard-card ${className}`.trim()}>
    <div className="particular-dashboard-card-header">
      <div>
        <h3>
          <i className={icon} />
          {title}
        </h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
    </div>
    {children}
  </section>
);

const ChartEmpty = ({ text = 'Sin datos para mostrar.' }) => (
  <div className="particular-chart-empty">{text}</div>
);

const LineChart = ({ data, valueKey = 'total' }) => {
  const rows = Array.isArray(data) ? data : [];
  const values = rows.map((item) => Number(item?.[valueKey]) || 0);
  const max = Math.max(1, ...values);
  const width = 420;
  const height = 204;
  const paddingLeft = 48;
  const paddingRight = 24;
  const paddingTop = 28;
  const paddingBottom = 36;
  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;
  const { axisMax, tickStep } = getLineChartScale(max);
  const ticks = getLineChartTicks(axisMax, tickStep);
  const getX = (index) => (rows.length > 1 ? paddingLeft + (chartWidth / (rows.length - 1)) * index : paddingLeft + chartWidth / 2);
  const getY = (value) => paddingTop + chartHeight - (Math.min(value, axisMax) / axisMax) * chartHeight;
  const points = values.map((value, index) => {
    const x = getX(index);
    const y = getY(value);
    return `${x},${y}`;
  }).join(' ');
  const markers = values.map((value, index) => {
    const item = rows[index] || {};
    const x = getX(index);
    const y = getY(value);
    const details = DASHBOARD_EVENT_BREAKDOWN
      .map((definition) => ({
        ...definition,
        value: Number(getDashboardProp(item, definition.key, definition.pascalKey)) || 0
      }))
      .filter((detail) => detail.value > 0);

    return {
      key: `${rows[index]?.fecha || index}-${value}`,
      label: item?.etiqueta || item?.fecha || 'Día',
      value,
      details,
      left: `${(x / width) * 100}%`,
      top: `${(y / height) * 100}%`
    };
  });

  if (!rows.length || values.every((value) => value === 0)) {
    return <ChartEmpty text="Todavía no hay actividad registrada." />;
  }

  return (
    <div className="particular-line-chart">
      <div className="particular-line-chart-plot">
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
          {ticks.map((tick) => {
            const y = getY(tick);
            return <line key={tick} x1={paddingLeft} y1={y} x2={width - paddingRight} y2={y} className="grid" />;
          })}
          <polyline points={points} />
        </svg>
        <div className="particular-line-chart-y-axis" aria-hidden="true">
          {ticks.map((tick) => (
            <span key={tick} style={{ top: `${(getY(tick) / height) * 100}%` }}>
              {formatDashboardNumber(tick)}
            </span>
          ))}
        </div>
        <div className="particular-line-chart-markers">
          {markers.map((marker) => (
            <button
              type="button"
              key={marker.key}
              className="particular-line-chart-marker"
              style={{ left: marker.left, top: marker.top }}
              aria-label={`${marker.label}: ${formatMetricWithLabel(marker.value, 'evento')}`}
            >
              <span className="particular-line-chart-tooltip">
                <strong>{marker.label}</strong>
                <em>{formatMetricWithLabel(marker.value, 'evento')}</em>
                {marker.details.length > 0 ? marker.details.map((detail) => (
                  <small key={detail.key}>
                    <span>{detail.label}</span>
                    <b>{formatDashboardNumber(detail.value)}</b>
                  </small>
                )) : (
                  <small>
                    <span>Sin detalle por tipo</span>
                    <b>0</b>
                  </small>
                )}
              </span>
            </button>
          ))}
        </div>
        <div className="particular-line-chart-values" aria-hidden="true">
          {markers.map((marker) => (
            <span key={marker.key} style={{ left: marker.left, top: marker.top }}>
              {formatDashboardNumber(marker.value)}
            </span>
          ))}
        </div>
      </div>
      <div
        className="particular-chart-labels"
        style={{
          gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))`,
          paddingLeft: `${(paddingLeft / width) * 100}%`,
          paddingRight: `${(paddingRight / width) * 100}%`
        }}
      >
        {rows.map((item) => <span key={item?.fecha || item?.etiqueta}>{item?.etiqueta || '-'}</span>)}
      </div>
    </div>
  );
};

const BarListChart = ({ data }) => {
  const rows = Array.isArray(data) ? data : [];
  const max = Math.max(1, ...rows.map((item) => Number(item?.total) || 0));

  if (!rows.length || rows.every((item) => !Number(item?.total))) {
    return <ChartEmpty text="Sin actividad en el rango." />;
  }

  return (
    <div className="particular-bar-list">
      {rows.map((item) => {
        const value = Number(item?.total) || 0;
        return (
          <div className="particular-bar-row" key={item?.key || item?.label}>
            <span>{item?.label || '-'}</span>
            <div className="particular-bar-track">
              <div style={{ width: `${Math.max(4, (value / max) * 100)}%`, backgroundColor: item?.color || '#2563eb' }} />
            </div>
            <strong>{formatDashboardNumber(value)}</strong>
          </div>
        );
      })}
    </div>
  );
};

const DonutChart = ({ data }) => {
  const rows = (Array.isArray(data) ? data : []).filter((item) => Number(item?.total) > 0);
  const total = rows.reduce((sum, item) => sum + (Number(item?.total) || 0), 0);
  const activeChannels = rows.length;
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  if (!total) {
    return <ChartEmpty text="Sin distribución disponible." />;
  }

  return (
    <div className="particular-donut-layout">
      <svg className="particular-donut" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={radius} className="base" />
        {rows.map((item) => {
          const length = ((Number(item.total) || 0) / total) * circumference;
          const dashOffset = -offset;
          offset += length;
          return (
            <circle
              key={item.key || item.label}
              cx="60"
              cy="60"
              r={radius}
              className="segment"
              stroke={item.color || '#2563eb'}
              strokeDasharray={`${length} ${circumference - length}`}
              strokeDashoffset={dashOffset}
            />
          );
        })}
        <text x="60" y="56">{formatDashboardNumber(activeChannels)}</text>
        <text x="60" y="74" className="caption">{activeChannels === 1 ? 'canal' : 'canales'}</text>
      </svg>
      <div className="particular-donut-legend">
        {rows.slice(0, 7).map((item) => (
          <span key={item.key || item.label} title={`${item.label}: ${formatMetricWithLabel(item.total, 'evento')}`}>
            <i style={{ backgroundColor: item.color || '#2563eb' }} />
            {item.label}
            <strong>{formatMetricWithLabel(item.total, 'evento')}</strong>
          </span>
        ))}
      </div>
    </div>
  );
};

const HourlyActivityChart = ({ data }) => {
  const rows = Array.isArray(data) ? data : [];
  const max = Math.max(1, ...rows.map((item) => Number(item?.total) || 0));

  if (!rows.length || rows.every((item) => !Number(item?.total))) {
    return <ChartEmpty text="Sin actividad registrada hoy." />;
  }

  return (
    <div className="particular-hour-chart">
      {rows.map((item, index) => {
        const total = Number(item?.total) || 0;
        return (
          <div className="particular-hour-bar" key={item?.hora || index}>
            <div style={{ height: `${Math.max(6, (total / max) * 100)}%` }} title={`${item?.hora}:00 - ${formatDashboardNumber(total)} eventos`} />
            {(index % 3 === 0 || index === rows.length - 1) && <span>{item?.hora}</span>}
          </div>
        );
      })}
    </div>
  );
};

const RankingList = ({ items, metricKey, metricLabel, secondary, emptyText, colorKey }) => {
  const rows = Array.isArray(items) ? items : [];
  const max = Math.max(1, ...rows.map((item) => Number(item?.[metricKey]) || 0));

  if (!rows.length) {
    return <ChartEmpty text={emptyText} />;
  }

  return (
    <div className="particular-ranking-list">
      {rows.map((item, index) => {
        const value = Number(item?.[metricKey]) || 0;
        const name = item?.nombre || item?.Nombre || item?.email || 'Sin nombre';
        return (
          <article className="particular-ranking-row" key={item?.id || `${name}-${index}`}>
            <div className="particular-ranking-index">{index + 1}</div>
            <div className="particular-ranking-main">
              <div>
                <strong>
                  {colorKey && <i className="particular-ranking-color" style={{ backgroundColor: item?.[colorKey] || '#2563eb' }} />}
                  {name}
                </strong>
                {secondary && <span>{secondary(item)}</span>}
              </div>
              <div className="particular-ranking-progress">
                <span style={{ width: `${Math.max(6, (value / max) * 100)}%` }} />
              </div>
            </div>
            <div className="particular-ranking-value">
              <strong>{formatDashboardNumber(value)}</strong>
              <span>{metricLabel}</span>
            </div>
          </article>
        );
      })}
    </div>
  );
};

const ParticularDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState('resumen');
  const [modalType, setModalType] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [contexto, setContexto] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState('');
  const [etiquetas, setEtiquetas] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [profesoresAsociados, setProfesoresAsociados] = useState([]);
  const [alumnos, setAlumnos] = useState([]);
  const [invitaciones, setInvitaciones] = useState([]);
  const [cuentaCorrienteMovimientos, setCuentaCorrienteMovimientos] = useState([]);
  const [cuentaCorrienteResumen, setCuentaCorrienteResumen] = useState(createCuentaCorrienteResumen);
  const [cuentaCorrienteTotal, setCuentaCorrienteTotal] = useState(0);
  const [cuentaCorrienteLoading, setCuentaCorrienteLoading] = useState(false);
  const [cuentaCorrienteFilters, setCuentaCorrienteFilters] = useState(createCuentaCorrienteFilters);
  const [cursos, setCursos] = useState([]);
  const [aulas, setAulas] = useState([]);
  const [asistentes, setAsistentes] = useState([]);
  const [etiquetaForm, setEtiquetaForm] = useState(emptyEtiquetaForm);
  const [profesorAsociadoForm, setProfesorAsociadoForm] = useState(emptyProfesorAsociadoForm);
  const [alumnoForm, setAlumnoForm] = useState(emptyAlumnoForm);
  const [invitacionForm, setInvitacionForm] = useState(emptyInvitacionForm);
  const [cuentaCorrienteForm, setCuentaCorrienteForm] = useState(emptyCuentaCorrienteForm);
  const [invitacionShare, setInvitacionShare] = useState(null);
  const [asistenteShare, setAsistenteShare] = useState(null);
  const [invitacionShareDownloading, setInvitacionShareDownloading] = useState(false);
  const [invitacionDetail, setInvitacionDetail] = useState(null);
  const [invitacionDetailLoading, setInvitacionDetailLoading] = useState(false);
  const [cursoForm, setCursoForm] = useState(emptyCursoForm);
  const [cursoToDelete, setCursoToDelete] = useState(null);
  const [deleteCursoConfirmText, setDeleteCursoConfirmText] = useState('');
  const [deleteCursoError, setDeleteCursoError] = useState('');
  const [aulaForm, setAulaForm] = useState(emptyAulaForm);
  const [selectedAulaAssistantId, setSelectedAulaAssistantId] = useState('');
  const [assignedAssistantIds, setAssignedAssistantIds] = useState([]);
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [courseDetailModal, setCourseDetailModal] = useState({ type: '', curso: null });
  const [courseDetailSearch, setCourseDetailSearch] = useState('');
  const [courseDetailRemovingKey, setCourseDetailRemovingKey] = useState('');
  const [aulaResourcesModal, setAulaResourcesModal] = useState({ open: false, aula: null });
  const [aulaStatsModal, setAulaStatsModal] = useState({ open: false, aula: null });
  const [resetPasswordModal, setResetPasswordModal] = useState({ type: '', target: null });
  const [subscriptionBlockedOpen, setSubscriptionBlockedOpen] = useState(false);
  const [subscriptionBlockedMessage, setSubscriptionBlockedMessage] = useState('');
  const [gridState, setGridState] = useState(createInitialGridState);
  const [solicitudesPendientes, setSolicitudesPendientes] = useState(0);

  const roleName = (user?.roleName || user?.nombreRol || user?.role || '').toLowerCase();
  const isAlumnoParticular = roleName.includes('alumnoparticular') || (roleName.includes('alumno') && roleName.includes('particular'));
  const isGeneralAdmin = roleName.includes('admin') && !isAdminColegio(user);
  const isProfesorParticularRole = roleName.includes('profesorparticular') || (roleName.includes('profesor') && roleName.includes('particular'));
  const canManageProfesoresByRole = roleName.includes('admin');
  const activeParticular = ParticularService.getActiveParticular();
  const contextParticular = contexto?.particular || contexto?.Particular || activeParticular;
  const chatGPTPermissionReady = isGeneralAdmin || Boolean(contextParticular);
  const canUseChatGPTFeatures = isParticularChatGPTAllowed(contextParticular);
  const calendarioPermissionReady = isGeneralAdmin || Boolean(contextParticular);
  const canUseCalendarioFeature = isParticularCalendarioAllowed(contextParticular);
  const particularName = getName(contextParticular, 'Particular');
  const loggedUserName = user?.displayName || user?.DisplayName || user?.name || user?.Nombre || user?.email || 'Usuario';
  const dashboardHeaderName = !isGeneralAdmin && isProfesorParticularRole ? loggedUserName : particularName;
  const dashboardSubtitle = dashboardHeaderName !== particularName ? `Cuenta particular: ${particularName}` : 'Profesores particulares';
  const puedeAdministrar = Boolean(contexto?.puedeAdministrar ?? contexto?.PuedeAdministrar ?? true);
  const puedeGestionarCatalogo = Boolean(
    contexto?.puedeGestionarCatalogo ??
    contexto?.PuedeGestionarCatalogo ??
    contexto?.puedeGestionarProfesores ??
    contexto?.PuedeGestionarProfesores ??
    false
  ) || isGeneralAdmin;
  const puedeGestionarProfesores = Boolean(contexto?.puedeGestionarProfesores ?? contexto?.PuedeGestionarProfesores ?? false) || canManageProfesoresByRole;
  const puedeGestionarInvitaciones = puedeGestionarProfesores;
  const puedeGestionarCuentaCorriente = Boolean(contexto?.puedeGestionarCuentaCorriente ?? contexto?.PuedeGestionarCuentaCorriente ?? false) || isGeneralAdmin;
  const isInitialLoading = isLoading && !contexto;
  const isRefreshing = isLoading && !isInitialLoading;
  const tableWrapperClassName = `particular-table-wrapper${isRefreshing ? ' is-loading' : ''}`;
  const hasMensajesAula = true;
  const hasArchivosAula = true;
  const hasLinksAula = true;
  const hasAgentesClasicos = true;
  const hasOpenAIChats = true;
  const hasPizarraAula = true;
  const hasChatAula = true;
  const hasVideollamadaAula = true;
  const solicitudesPollMinutes = useMemo(() => {
    const raw = process.env.REACT_APP_ADMIN_SOLICITUDES_POLL_MINUTES;
    const parsed = raw ? Number(raw) : 1;
    if (!Number.isFinite(parsed) || parsed <= 0) return 1;
    return parsed;
  }, []);
  const handleChangeParticular = useCallback(() => {
    ParticularService.setActiveParticular(null);
    navigate('/admin/select-colegio', { replace: true, state: { scope: 'particulares' } });
  }, [navigate]);
  const buildInvitationLink = useCallback((invitacionOrCode) => {
    const code = typeof invitacionOrCode === 'string' ? invitacionOrCode : getInvitationCode(invitacionOrCode);
    const safeCode = encodeURIComponent(code || '');
    return `${getPublicAppOrigin()}/particular/invitacion-alumno/${safeCode}`;
  }, []);

  const menuItems = useMemo(() => buildParticularDashboardMenuItems(), []);

  useEffect(() => {
    const requestedTab = location.state?.tab || location.state?.activeMenu;
    if (!requestedTab) return;

    const requestedItem = menuItems.find((item) => item.id === requestedTab);
    if (requestedItem) {
      if (isParticularChatGPTMenuItem(requestedItem) && !canUseChatGPTFeatures) {
        if (!chatGPTPermissionReady) return;
        setActiveMenu('resumen');
        setSubscriptionBlockedMessage('');
        setSubscriptionBlockedOpen(true);
        return;
      }
      if (isParticularCalendarioMenuItem(requestedItem) && !canUseCalendarioFeature) {
        if (!calendarioPermissionReady) return;
        setActiveMenu('resumen');
        setSubscriptionBlockedMessage(CALENDARIO_SUBSCRIPTION_BLOCKED_MESSAGE);
        setSubscriptionBlockedOpen(true);
        return;
      }
      setActiveMenu(requestedTab);
    }
  }, [calendarioPermissionReady, canUseCalendarioFeature, canUseChatGPTFeatures, chatGPTPermissionReady, location.state, menuItems]);

  useEffect(() => {
    if (isParticularChatGPTMenuItem({ id: activeMenu }) && chatGPTPermissionReady && !canUseChatGPTFeatures) {
      setActiveMenu('resumen');
      setSubscriptionBlockedMessage('');
      setSubscriptionBlockedOpen(true);
      return;
    }

    if (isParticularCalendarioMenuItem({ id: activeMenu }) && calendarioPermissionReady && !canUseCalendarioFeature) {
      setActiveMenu('resumen');
      setSubscriptionBlockedMessage(CALENDARIO_SUBSCRIPTION_BLOCKED_MESSAGE);
      setSubscriptionBlockedOpen(true);
    }
  }, [activeMenu, calendarioPermissionReady, canUseCalendarioFeature, canUseChatGPTFeatures, chatGPTPermissionReady]);

  const stats = useMemo(() => ({
    etiquetas: etiquetas.length,
    profesores: profesores.length,
    profesoresAsociados: profesoresAsociados.length,
    alumnos: alumnos.length,
    invitaciones: invitaciones.length,
    cursos: cursos.length,
    aulas: aulas.length,
    asistentes: asistentes.length
  }), [alumnos.length, asistentes.length, aulas.length, cursos.length, etiquetas.length, invitaciones.length, profesores.length, profesoresAsociados.length]);

  const invitationStats = useMemo(() => {
    const initial = {
      total: invitaciones.length,
      activas: 0,
      caducadas: 0,
      inactivas: 0,
      registros: 0,
      sinRegistros: 0,
      top: []
    };

    const top = invitaciones
      .map((invitacion) => {
        const estado = getInvitationStatus(invitacion);
        const registros = Number(getInvitationCount(invitacion)) || 0;
        const estadoNormalizado = normalizeSearchText(estado);
        if (estadoNormalizado === 'activa') initial.activas += 1;
        if (estadoNormalizado === 'caducada') initial.caducadas += 1;
        if (estadoNormalizado === 'inactiva') initial.inactivas += 1;
        initial.registros += registros;
        if (registros === 0) initial.sinRegistros += 1;
        return {
          id: getId(invitacion),
          nombre: getInvitationCode(invitacion) || 'Sin código',
          codigo: getInvitationCode(invitacion),
          estado,
          fechaCaducidadUtc: getInvitationExpiration(invitacion),
          registros
        };
      })
      .sort((a, b) => b.registros - a.registros || String(a.fechaCaducidadUtc || '').localeCompare(String(b.fechaCaducidadUtc || '')))
      .slice(0, 8);

    return { ...initial, top };
  }, [invitaciones]);

  const etiquetaSelectOptions = useMemo(() => etiquetas.map((etiqueta) => ({
    value: getId(etiqueta),
    label: getName(etiqueta),
    searchText: getEtiquetaSearchText(etiqueta),
    color: etiqueta?.color || etiqueta?.Color || '#2563eb'
  })), [etiquetas]);

  const profesorSelectOptions = useMemo(() => profesores.map((profesor) => ({
    value: getId(profesor),
    label: getFullName(profesor),
    secondary: getEmail(profesor),
    searchText: joinSearchValues([getFullName(profesor), getEmail(profesor)])
  })), [profesores]);

  const alumnoSelectOptions = useMemo(() => alumnos.map((alumno) => ({
    value: getId(alumno),
    label: getFullName(alumno),
    secondary: getEmail(alumno),
    searchText: getAlumnoSearchText(alumno)
  })), [alumnos]);

  const cursoSelectOptions = useMemo(() => cursos.map((curso) => ({
    value: getId(curso),
    label: getName(curso),
    secondary: getDescripcion(curso),
    searchText: getCursoSearchText(curso)
  })), [cursos]);

  const aulaSelectOptions = useMemo(() => aulas.map((aula) => ({
    value: getId(aula),
    label: getName(aula),
    secondary: aula?.cursoNombre || aula?.CursoNombre || 'Curso',
    searchText: getAulaSearchText(aula)
  })), [aulas]);

  const asistentesListos = useMemo(() => (
    asistentes.filter((asistente) => (
      getBool(asistente, 'gptClasicoCreacionFinalizada', 'GPTClasicoCreacionFinalizada', false) &&
      Boolean(getAssistantUrl(asistente))
    ))
  ), [asistentes]);

  const asistenteSelectOptions = useMemo(() => asistentesListos.map((asistente) => ({
    value: getAssistantId(asistente),
    label: getAssistantName(asistente),
    secondary: getAssistantDescription(asistente),
    searchText: getAssistantSearchText(asistente)
  })), [asistentesListos]);

  const particularMensajesService = useMemo(() => ({
    getMensajes: (aulaId, options) => particularesApiService.getAulaMensajes(aulaId, options),
    crearMensaje: (aulaId, payload) => particularesApiService.createAulaMensaje(aulaId, payload),
    actualizarMensaje: (aulaId, mensajeId, payload) => particularesApiService.updateAulaMensaje(aulaId, mensajeId, payload),
    eliminarMensaje: (aulaId, mensajeId) => particularesApiService.deleteAulaMensaje(aulaId, mensajeId)
  }), []);

  const particularArchivosService = useMemo(() => ({
    list: (aulaId) => particularesApiService.getAulaArchivos(aulaId),
    upload: (aulaId, file) => particularesApiService.uploadAulaArchivo(aulaId, file),
    delete: (aulaId, archivoId) => particularesApiService.deleteAulaArchivo(aulaId, archivoId),
    downloadZip: (aulaId) => particularesApiService.downloadAulaArchivoZip(aulaId),
    downloadFile: (aulaId, archivoId) => particularesApiService.downloadAulaArchivo(aulaId, archivoId)
  }), []);

  const particularLinksService = useMemo(() => ({
    list: (aulaId) => particularesApiService.getAulaLinks(aulaId),
    create: (aulaId, payload) => particularesApiService.createAulaLink(aulaId, payload),
    update: (aulaId, linkId, payload) => particularesApiService.updateAulaLink(aulaId, linkId, payload),
    delete: (aulaId, linkId) => particularesApiService.deleteAulaLink(aulaId, linkId)
  }), []);

  const particularAsistentesService = useMemo(() => ({
    getAulaAsistentesIA: (aulaId) => particularesApiService.getAulaAsistentesIA(aulaId),
    addAulaAsistenteIA: (aulaId, assistantId) => particularesApiService.addAulaAsistenteIA(aulaId, assistantId),
    removeAulaAsistenteIA: (aulaId, assistantId) => particularesApiService.removeAulaAsistenteIA(aulaId, assistantId)
  }), []);

  const getCursoAulas = useCallback((curso) => {
    const cursoId = getId(curso);
    const cursoNombre = normalizeSearchText(getName(curso, ''));

    return aulas.filter((aula) => {
      const aulaCursoId = String(aula?.idCursoParticular || aula?.IdCursoParticular || '');
      if (cursoId && aulaCursoId) return aulaCursoId === cursoId;
      return cursoNombre && normalizeSearchText(aula?.cursoNombre || aula?.CursoNombre || '') === cursoNombre;
    });
  }, [aulas]);

  const updateGridState = useCallback((gridKey, patch) => {
    setGridState((prev) => ({
      ...prev,
      [gridKey]: {
        ...createGridState(),
        ...(prev[gridKey] || {}),
        ...patch
      }
    }));
  }, []);

  const updateGridSearch = useCallback((gridKey, search) => {
    updateGridState(gridKey, { search, page: 1 });
  }, [updateGridState]);

  const updateGridPageSize = useCallback((gridKey, pageSize) => {
    updateGridState(gridKey, { pageSize, page: 1 });
  }, [updateGridState]);

  const updateGridPage = useCallback((gridKey, page) => {
    updateGridState(gridKey, { page });
  }, [updateGridState]);

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    setDashboardError('');
    try {
      const response = await particularesApiService.getDashboard();
      setDashboardData(response);
    } catch (error) {
      setDashboardError(error.message || 'No pudimos cargar el dashboard.');
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  const loadCuentaCorriente = useCallback(async () => {
    if (!puedeGestionarCuentaCorriente) return;
    setCuentaCorrienteLoading(true);
    try {
      const response = await particularesApiService.getCuentaCorriente({
        search: cuentaCorrienteFilters.search,
        fechaDesde: cuentaCorrienteFilters.fechaDesde,
        fechaHasta: cuentaCorrienteFilters.fechaHasta,
        tipoMovimiento: cuentaCorrienteFilters.tipoMovimiento,
        tipoEntidad: cuentaCorrienteFilters.tipoEntidad,
        idUsuarioEntidad: cuentaCorrienteFilters.idUsuarioEntidad,
        skip: (Math.max(cuentaCorrienteFilters.page || 1, 1) - 1) * (cuentaCorrienteFilters.pageSize || DEFAULT_GRID_PAGE_SIZE),
        take: cuentaCorrienteFilters.pageSize || DEFAULT_GRID_PAGE_SIZE
      });
      setCuentaCorrienteMovimientos(getList(response, 'movimientos', 'Movimientos'));
      setCuentaCorrienteResumen(response?.resumen || response?.Resumen || createCuentaCorrienteResumen());
      setCuentaCorrienteTotal(Number(response?.total ?? response?.Total ?? 0) || 0);
    } catch (error) {
      showError(error, 'No pudimos cargar la cuenta corriente.');
    } finally {
      setCuentaCorrienteLoading(false);
    }
  }, [cuentaCorrienteFilters, puedeGestionarCuentaCorriente]);

  const loadData = useCallback(async (options = {}) => {
    const preserveAlert = Boolean(options?.preserveAlert);
    setIsLoading(true);
    setDashboardLoading(true);
    if (!preserveAlert) {
      setAlert((prev) => ({ ...prev, show: false }));
    }
    try {
      const contextoRes = await particularesApiService.getContexto();
      const contextoParticular = contextoRes?.particular || contextoRes?.Particular || null;
      const canLoadProfesores = Boolean(contextoRes?.puedeGestionarProfesores ?? contextoRes?.PuedeGestionarProfesores ?? false) || canManageProfesoresByRole;
      const canLoadInvitaciones = canLoadProfesores;
      const canLoadAsistentes = isParticularChatGPTAllowed(contextoParticular);
      const [
        etiquetasRes,
        profesoresRes,
        profesoresAsociadosRes,
        alumnosRes,
        invitacionesRes,
        cursosRes,
        aulasRes,
        asistentesRes,
        dashboardRes
      ] = await Promise.all([
        particularesApiService.getEtiquetas(),
        particularesApiService.getProfesores(),
        canLoadProfesores ? particularesApiService.getProfesoresAsociados() : Promise.resolve({ profesores: [] }),
        particularesApiService.getAlumnos(),
        canLoadInvitaciones ? particularesApiService.getAlumnoInvitaciones() : Promise.resolve({ invitaciones: [] }),
        particularesApiService.getCursos(),
        particularesApiService.getAulas(),
        canLoadAsistentes ? particularesApiService.getAsistentes() : Promise.resolve({ asistentes: [] }),
        particularesApiService.getDashboard()
          .then((data) => ({ ok: true, data }))
          .catch((error) => ({ ok: false, error }))
      ]);

      setContexto(contextoRes);
      setEtiquetas(getList(etiquetasRes, 'etiquetas', 'Etiquetas'));
      setProfesores(getList(profesoresRes, 'profesores', 'Profesores'));
      setProfesoresAsociados(getList(profesoresAsociadosRes, 'profesores', 'Profesores'));
      setAlumnos(getList(alumnosRes, 'alumnos', 'Alumnos'));
      setInvitaciones(getList(invitacionesRes, 'invitaciones', 'Invitaciones'));
      setCursos(getList(cursosRes, 'cursos', 'Cursos'));
      setAulas(getList(aulasRes, 'aulas', 'Aulas'));
      setAsistentes(getList(asistentesRes, 'asistentes', 'Asistentes'));
      if (dashboardRes.ok) {
        setDashboardData(dashboardRes.data);
        setDashboardError('');
      } else {
        setDashboardError(dashboardRes.error?.message || 'No pudimos cargar el dashboard.');
      }
    } catch (error) {
      setAlert({
        show: true,
        title: 'No pudimos cargar el particular',
        message: error.message || 'Intenta nuevamente.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
      setDashboardLoading(false);
    }
  }, [canManageProfesoresByRole]);

  const refreshAfterMutation = useCallback(() => {
    setTimeout(() => {
      loadData({ preserveAlert: true });
    }, 0);
  }, [loadData]);

  const loadSolicitudesPendientes = useCallback(async () => {
    if (!isGeneralAdmin) {
      setSolicitudesPendientes(0);
      return;
    }

    try {
      const response = await particularesApiService.getSolicitudesRecibidasGptClasicos({
        skip: 0,
        take: 1,
        leido: 'todos',
        tipo: 'todos'
      });
      const pendientes = response?.totalPendientes ?? response?.TotalPendientes ?? 0;
      setSolicitudesPendientes(Number.isFinite(pendientes) ? pendientes : 0);
    } catch {
      setSolicitudesPendientes(0);
    }
  }, [isGeneralAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeMenu !== 'cuentaCorriente' || !puedeGestionarCuentaCorriente) return;
    loadCuentaCorriente();
  }, [activeMenu, loadCuentaCorriente, puedeGestionarCuentaCorriente]);

  useEffect(() => {
    if (!isGeneralAdmin) return undefined;

    loadSolicitudesPendientes();
    const intervalMs = solicitudesPollMinutes * 60 * 1000;
    const intervalId = setInterval(loadSolicitudesPendientes, intervalMs);
    return () => clearInterval(intervalId);
  }, [isGeneralAdmin, loadSolicitudesPendientes, solicitudesPollMinutes]);

  useEffect(() => {
    if (!selectedAulaAssistantId && aulas.length > 0) {
      setSelectedAulaAssistantId(getId(aulas[0]));
    }
  }, [aulas, selectedAulaAssistantId]);

  useEffect(() => {
    if (modalType !== 'asistentes' || !selectedAulaAssistantId) {
      if (modalType !== 'asistentes') setAssignedAssistantIds([]);
      return;
    }

    let mounted = true;
    const loadAssignments = async () => {
      setAssistantLoading(true);
      try {
        const response = await particularesApiService.getAulaAsistentes(selectedAulaAssistantId);
        const ids = response?.asistentesAsignados || response?.AsistentesAsignados || [];
        if (mounted) setAssignedAssistantIds(Array.isArray(ids) ? ids : []);
      } catch (error) {
        if (mounted) {
          setAlert({
            show: true,
            title: 'No pudimos cargar los asistentes del aula',
            message: error.message || 'Intenta nuevamente.',
            type: 'error'
          });
        }
      } finally {
        if (mounted) setAssistantLoading(false);
      }
    };

    loadAssignments();
    return () => {
      mounted = false;
    };
  }, [modalType, selectedAulaAssistantId]);

  const showSuccess = (message) => {
    setAlert({ show: true, title: 'Listo', message, type: 'success' });
  };

  const showError = (error, fallback) => {
    setAlert({ show: true, title: 'No se pudo completar', message: error.message || fallback, type: 'error' });
  };

  const closeModal = () => {
    if (saving) return;
    setModalType(null);
    setCourseDetailModal({ type: '', curso: null });
    setCourseDetailSearch('');
    setCourseDetailRemovingKey('');
    setInvitacionShare(null);
    setAsistenteShare(null);
    setInvitacionShareDownloading(false);
    setInvitacionDetail(null);
    setInvitacionDetailLoading(false);
    setCuentaCorrienteForm(emptyCuentaCorrienteForm);
    setResetPasswordModal({ type: '', target: null });
    setCursoToDelete(null);
    setDeleteCursoConfirmText('');
    setDeleteCursoError('');
  };

  const getResetPasswordInfo = (type, target) => {
    const isAlumno = type === 'alumno';
    const userLabel = isAlumno ? 'alumno' : 'profesor';
    const dni = getDni(target);
    const newPassword = dni || (isAlumno ? '123456' : '');

    return {
      title: `Resetear contraseña del ${userLabel}`,
      icon: isAlumno ? 'fas fa-user-graduate' : 'fas fa-user-tie',
      userLabel,
      name: getFullName(target, getEmail(target) || userLabel),
      email: getEmail(target),
      dni,
      newPassword,
      passwordSource: dni ? 'DNI registrado' : isAlumno ? 'password por defecto' : 'DNI requerido',
      canConfirm: Boolean(newPassword)
    };
  };

  const openResetPasswordModal = (type, target) => {
    setResetPasswordModal({ type, target });
    setModalType('resetPassword');
  };

  const openEtiquetaModal = (etiqueta = null) => {
    if (!puedeGestionarCatalogo) return;
    setEtiquetaForm(etiqueta ? {
      id: getId(etiqueta),
      nombre: getName(etiqueta, ''),
      descripcion: getDescripcion(etiqueta),
      color: etiqueta?.color || etiqueta?.Color || '#2563eb'
    } : emptyEtiquetaForm);
    setModalType('etiqueta');
  };

  const openProfesorAsociadoModal = (profesorAsociado = null) => {
    setProfesorAsociadoForm(profesorAsociado ? {
      id: getId(profesorAsociado),
      email: getEmail(profesorAsociado),
      nombre: profesorAsociado?.nombre || profesorAsociado?.Nombre || '',
      apellido: profesorAsociado?.apellido || profesorAsociado?.Apellido || '',
      telefono: profesorAsociado?.telefono || profesorAsociado?.Telefono || '',
      dni: profesorAsociado?.dni || profesorAsociado?.Dni || '',
      activo: getBool(profesorAsociado, 'activo', 'Activo', true)
    } : emptyProfesorAsociadoForm);
    setModalType('profesorAsociado');
  };

  const openAlumnoModal = (alumno = null) => {
    setAlumnoForm(alumno ? {
      id: getId(alumno),
      email: getEmail(alumno),
      nombre: alumno?.nombre || alumno?.Nombre || '',
      apellido: alumno?.apellido || alumno?.Apellido || '',
      telefono: alumno?.telefono || alumno?.Telefono || '',
      dni: alumno?.dni || alumno?.Dni || '',
      activo: getBool(alumno, 'activo', 'Activo', true)
    } : emptyAlumnoForm);
    setModalType('alumno');
  };

  const openInvitacionModal = (invitacion = null) => {
    if (!puedeGestionarInvitaciones) return;
    setInvitacionForm(invitacion ? {
      id: getId(invitacion),
      codigo: getInvitationCode(invitacion),
      fechaCaducidad: formatDateInput(getInvitationExpiration(invitacion)),
      activo: getBool(invitacion, 'activo', 'Activo', true)
    } : {
      ...emptyInvitacionForm,
      codigo: buildRandomInvitationCode(),
      fechaCaducidad: getTomorrowDateInput(),
      activo: true
    });
    setModalType('invitacion');
  };

  const openInvitacionShareModal = (invitacion) => {
    if (!invitacion) return;
    setInvitacionShare({
      ...invitacion,
      link: buildInvitationLink(invitacion)
    });
    setModalType('invitacionShare');
  };

  const openInvitacionDetailModal = async (invitacion) => {
    const id = getId(invitacion);
    if (!id) return;
    setInvitacionDetail(invitacion);
    setInvitacionDetailLoading(true);
    setModalType('invitacionDetalle');
    try {
      const response = await particularesApiService.getAlumnoInvitacion(id);
      setInvitacionDetail(response?.invitacion || response?.Invitacion || invitacion);
    } catch (error) {
      showError(error, 'No pudimos cargar el detalle de la invitación.');
    } finally {
      setInvitacionDetailLoading(false);
    }
  };

  const updateCuentaCorrienteFilters = (patch) => {
    setCuentaCorrienteFilters((prev) => ({
      ...prev,
      ...patch,
      page: patch.page || 1
    }));
  };

  const openCuentaCorrienteMovimientoModal = (movimiento = null, preset = {}) => {
    if (!puedeGestionarCuentaCorriente) return;
    const tipoEntidad = movimiento ? getMovimientoEntidadTipo(movimiento) : (preset.tipoEntidad || 'Alumno');
    const idUsuarioEntidad = movimiento
      ? (movimiento?.idUsuarioEntidad || movimiento?.IdUsuarioEntidad || '')
      : (preset.idUsuarioEntidad || '');
    setCuentaCorrienteForm(movimiento ? {
      id: getId(movimiento),
      tipoMovimiento: getMovimientoTipo(movimiento) || 'Debito',
      tipoEntidad: tipoEntidad || 'Alumno',
      idUsuarioEntidad: tipoEntidad === 'Empresa' ? '' : String(idUsuarioEntidad || ''),
      concepto: getMovimientoConcepto(movimiento),
      notas: getMovimientoNotas(movimiento),
      importe: formatCuentaCorrienteAmountFromNumber(getMovimientoImporte(movimiento)),
      fechaMovimiento: formatDateInput(getMovimientoFecha(movimiento)) || getTodayDateInput()
    } : {
      ...emptyCuentaCorrienteForm,
      tipoEntidad,
      idUsuarioEntidad: tipoEntidad === 'Empresa' ? '' : String(idUsuarioEntidad || ''),
      fechaMovimiento: getTodayDateInput()
    });
    setModalType('cuentaCorrienteMovimiento');
  };

  const copyText = async (value, successMessage = 'Copiado al portapapeles.') => {
    try {
      await navigator.clipboard.writeText(value);
      showSuccess(successMessage);
    } catch {
      showError({ message: 'No pudimos copiar automáticamente. Seleccioná el texto y copialo manualmente.' }, 'No pudimos copiar el texto.');
    }
  };

  const buildAssistantPublicLink = (asistente) => {
    const rawUrl = getAssistantUrl(asistente);
    const trimmed = String(rawUrl || '').trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    return `${window.location.origin}/aula?asistente=${encodeURIComponent(trimmed)}`;
  };

  const openAssistantEdit = (asistente) => {
    const assistantId = getAssistantId(asistente);
    if (!assistantId) {
      showError({ message: 'No encontramos el identificador interno de este asistente IA.' }, 'No pudimos abrir la edición.');
      return;
    }
    navigate(`/mis-agentes-clasicos-gpt/editar/${assistantId}`, { state: { scope: 'particulares' } });
  };

  const openAssistantPublicLink = (asistente) => {
    const link = buildAssistantPublicLink(asistente);
    if (!link) return;
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const openAssistantShareModal = async (asistente) => {
    const link = buildAssistantPublicLink(asistente);
    if (!link) {
      showError({ message: 'Este asistente IA todavía no tiene un link público para compartir.' }, 'No pudimos compartir el asistente IA.');
      return;
    }

    try {
      await navigator.clipboard.writeText(link);
      showSuccess('Link copiado.');
    } catch {
      // El modal permite copiar manualmente si el navegador bloquea el portapapeles.
    }

    setAsistenteShare({
      asistente,
      link
    });
    setModalType('asistenteShare');
  };

  const handleDownloadInvitationImage = async () => {
    const code = getInvitationCode(invitacionShare);
    const link = invitacionShare?.link || buildInvitationLink(code);
    if (!code || !link) return;

    setInvitacionShareDownloading(true);
    try {
      const qrUrl = `https://quickchart.io/qr?text=${encodeURIComponent(link)}&size=360&margin=2&ecLevel=M`;
      const [logoDataUrl, qrDataUrl] = await Promise.all([
        fetchAssetAsDataUrl(brandLogo),
        fetchAssetAsDataUrl(qrUrl)
      ]);

      const svgMarkup = buildInvitationShareCardSvg({
        particularName,
        code,
        link,
        qrDataUrl,
        logoDataUrl
      });
      const svgBlob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);

      try {
        const image = await loadImage(svgUrl);
        const canvas = document.createElement('canvas');
        canvas.width = INVITATION_SHARE_CARD_WIDTH;
        canvas.height = INVITATION_SHARE_CARD_HEIGHT;
        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('No pudimos inicializar la imagen para descarga.');
        }

        context.fillStyle = '#0f172a';
        context.fillRect(0, 0, INVITATION_SHARE_CARD_WIDTH, INVITATION_SHARE_CARD_HEIGHT);
        context.drawImage(image, 0, 0, INVITATION_SHARE_CARD_WIDTH, INVITATION_SHARE_CARD_HEIGHT);

        const pngBlob = await new Promise((resolve, reject) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('No pudimos convertir la invitación a PNG.'));
                return;
              }
              resolve(blob);
            },
            'image/png',
            1
          );
        });

        downloadBlob(pngBlob, buildInvitationShareFileName(particularName, code));
      } finally {
        URL.revokeObjectURL(svgUrl);
      }

      showSuccess('Imagen de invitación descargada.');
    } catch (error) {
      showError(error, 'No pudimos generar la imagen de la invitación.');
    } finally {
      setInvitacionShareDownloading(false);
    }
  };

  const openCursoModal = (curso = null) => {
    if (!puedeGestionarCatalogo) return;
    setCursoForm(curso ? {
      id: getId(curso),
      nombre: getName(curso, ''),
      descripcion: getDescripcion(curso),
      etiquetaIds: normalizeIds(curso?.etiquetas || curso?.Etiquetas || []),
      profesorIds: normalizeIds(curso?.profesores || curso?.Profesores || []),
      alumnoIds: normalizeIds(curso?.alumnos || curso?.Alumnos || [])
    } : emptyCursoForm);
    setModalType('curso');
  };

  const openDeleteCursoModal = (curso) => {
    if (!puedeGestionarCatalogo) return;
    setCursoToDelete(curso);
    setDeleteCursoConfirmText('');
    setDeleteCursoError('');
    setModalType('deleteCurso');
  };

  const openCursoDetailModal = (type, curso) => {
    setCourseDetailModal({ type, curso });
    setCourseDetailSearch('');
    setModalType('cursoDetalle');
  };

  const openAulaModal = (aula = null) => {
    setAulaForm(aula ? {
      id: getId(aula),
      idCursoParticular: String(aula?.idCursoParticular || aula?.IdCursoParticular || ''),
      nombre: getName(aula, ''),
      descripcion: getDescripcion(aula),
      aulaPublica: false,
      mostrarArchivos: getBool(aula, 'mostrarArchivos', 'MostrarArchivos', true),
      mostrarLinks: getBool(aula, 'mostrarLinks', 'MostrarLinks', true),
      mostrarPizarra: getBool(aula, 'mostrarPizarra', 'MostrarPizarra', true),
      mostrarChat: getBool(aula, 'mostrarChat', 'MostrarChat', true),
      mostrarVideollamada: getBool(aula, 'mostrarVideollamada', 'MostrarVideollamada', true),
      mostrarForo: getBool(aula, 'mostrarForo', 'MostrarForo', true),
      mostrarAsistentesIA: getBool(aula, 'mostrarAsistentesIA', 'MostrarAsistentesIA', true)
    } : emptyAulaForm);
    setModalType('aula');
  };

  const openAsistentesModal = (aula = null) => {
    const aulaId = aula ? getId(aula) : selectedAulaAssistantId || getId(aulas[0]);
    setSelectedAulaAssistantId(aulaId);
    setModalType('asistentes');
  };

  const openAulaResourcesModal = (aula) => {
    setModalType(null);
    setCourseDetailModal({ type: '', curso: null });
    setCourseDetailSearch('');
    setAulaResourcesModal({ open: true, aula });
  };

  const openAulaStatsModal = (aula) => {
    setModalType(null);
    setCourseDetailModal({ type: '', curso: null });
    setCourseDetailSearch('');
    setAulaStatsModal({ open: true, aula });
  };

  const loadAulaStats = useCallback((id, options) => particularesApiService.getAulaStats(id, options), []);

  const handleAulaUpdated = useCallback((updatedAula) => {
    const updatedId = getId(updatedAula);
    if (!updatedId) return;

    setAulaResourcesModal((prev) => (
      prev.aula && getId(prev.aula) === updatedId
        ? { ...prev, aula: { ...prev.aula, ...updatedAula } }
        : prev
    ));
    setAulas((prev) => prev.map((aula) => (
      getId(aula) === updatedId ? { ...aula, ...updatedAula } : aula
    )));
  }, []);

  const handleMenuNavigate = (item) => {
    if (!item) return;
    setIsSidebarOpen(false);
    if (isParticularChatGPTMenuItem(item) && !canUseChatGPTFeatures) {
      setSubscriptionBlockedMessage('');
      setSubscriptionBlockedOpen(true);
      return;
    }
    if (isParticularCalendarioMenuItem(item) && !canUseCalendarioFeature) {
      setSubscriptionBlockedMessage(CALENDARIO_SUBSCRIPTION_BLOCKED_MESSAGE);
      setSubscriptionBlockedOpen(true);
      return;
    }
    if (item.path) {
      navigate(item.path, item.state ? { state: item.state } : undefined);
      return;
    }
    setActiveMenu(item.id);
  };

  const saveEtiqueta = async (event) => {
    event.preventDefault();
    if (!puedeGestionarCatalogo) {
      showError({ message: 'No tenés permisos para administrar etiquetas.' }, 'No tenés permisos para administrar etiquetas.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nombre: etiquetaForm.nombre,
        descripcion: etiquetaForm.descripcion,
        color: etiquetaForm.color
      };
      const successMessage = etiquetaForm.id ? 'Etiqueta actualizada.' : 'Etiqueta creada.';
      if (etiquetaForm.id) {
        await particularesApiService.updateEtiqueta(etiquetaForm.id, payload);
      } else {
        await particularesApiService.createEtiqueta(payload);
      }
      setEtiquetaForm(emptyEtiquetaForm);
      setModalType(null);
      showSuccess(successMessage);
      refreshAfterMutation();
    } catch (error) {
      showError(error, 'No pudimos guardar la etiqueta.');
    } finally {
      setSaving(false);
    }
  };

  const deleteEtiqueta = async (etiqueta) => {
    const id = getId(etiqueta);
    if (!id) return;
    if (!puedeGestionarCatalogo) {
      showError({ message: 'No tenés permisos para administrar etiquetas.' }, 'No tenés permisos para administrar etiquetas.');
      return;
    }
    setSaving(true);
    try {
      await particularesApiService.deleteEtiqueta(id);
      showSuccess('Etiqueta eliminada.');
      refreshAfterMutation();
    } catch (error) {
      showError(error, 'No pudimos eliminar la etiqueta.');
    } finally {
      setSaving(false);
    }
  };

  const saveProfesorAsociado = async (event) => {
    event.preventDefault();
    const isCreating = !profesorAsociadoForm.id;
    setSaving(true);
    try {
      const payload = {
        email: profesorAsociadoForm.email,
        nombre: profesorAsociadoForm.nombre,
        apellido: profesorAsociadoForm.apellido,
        telefono: profesorAsociadoForm.telefono,
        dni: profesorAsociadoForm.dni,
        activo: profesorAsociadoForm.activo
      };
      const successMessage = profesorAsociadoForm.id ? 'Profesor actualizado.' : 'Profesor creado.';
      if (profesorAsociadoForm.id) {
        await particularesApiService.updateProfesorAsociado(profesorAsociadoForm.id, payload);
      } else {
        await particularesApiService.createProfesorAsociado(payload);
      }
      setProfesorAsociadoForm(emptyProfesorAsociadoForm);
      setModalType(null);
      showSuccess(successMessage);
      refreshAfterMutation();
    } catch (error) {
      if (isCreating && isSubscriptionLimitError(error)) {
        setModalType(null);
      }
      showError(error, 'No pudimos guardar el profesor.');
    } finally {
      setSaving(false);
    }
  };

  const toggleProfesorAsociadoActivo = async (profesorAsociado) => {
    const id = getId(profesorAsociado);
    if (!id) return;
    setSaving(true);
    try {
      await particularesApiService.updateProfesorAsociadoActivo(id, !getBool(profesorAsociado, 'activo', 'Activo', true));
      showSuccess('Estado del profesor actualizado.');
      refreshAfterMutation();
    } catch (error) {
      showError(error, 'No pudimos actualizar el profesor.');
    } finally {
      setSaving(false);
    }
  };

  const resetProfesorAsociadoPassword = async () => {
    const { target } = resetPasswordModal;
    const id = getId(target);
    if (!id) return;
    const resetInfo = getResetPasswordInfo('profesor', target);
    if (!resetInfo.canConfirm) {
      showError({ message: 'El profesor no tiene DNI registrado. Cargá el DNI antes de resetear la contraseña.' }, 'No pudimos resetear la contraseña.');
      return;
    }
    setSaving(true);
    try {
      await particularesApiService.resetProfesorAsociadoPassword(id);
      setModalType(null);
      setResetPasswordModal({ type: '', target: null });
      showSuccess(`Contraseña reseteada. Nuevo password: ${resetInfo.newPassword}.`);
    } catch (error) {
      setModalType(null);
      setResetPasswordModal({ type: '', target: null });
      showError(error, 'No pudimos resetear la contraseña.');
    } finally {
      setSaving(false);
    }
  };

  const saveAlumno = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        email: alumnoForm.email,
        nombre: alumnoForm.nombre,
        apellido: alumnoForm.apellido,
        telefono: alumnoForm.telefono,
        dni: alumnoForm.dni,
        activo: alumnoForm.activo
      };
      const successMessage = alumnoForm.id ? 'Alumno actualizado.' : 'Alumno creado.';
      if (alumnoForm.id) {
        await particularesApiService.updateAlumno(alumnoForm.id, payload);
      } else {
        await particularesApiService.createAlumno(payload);
      }
      setAlumnoForm(emptyAlumnoForm);
      setModalType(null);
      showSuccess(successMessage);
      refreshAfterMutation();
    } catch (error) {
      showError(error, 'No pudimos guardar el alumno.');
    } finally {
      setSaving(false);
    }
  };

  const saveInvitacion = async (event) => {
    event.preventDefault();
    if (!puedeGestionarInvitaciones) {
      showError({ message: 'No tenés permisos para administrar invitaciones.' }, 'No tenés permisos para administrar invitaciones.');
      return;
    }
    if (!String(invitacionForm.codigo || '').trim() || String(invitacionForm.codigo || '').trim().length < 6) {
      showError({ message: 'El código debe tener al menos 6 caracteres.' }, 'El código debe tener al menos 6 caracteres.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        codigo: invitacionForm.codigo,
        fechaCaducidad: invitacionForm.fechaCaducidad,
        activo: invitacionForm.activo
      };
      let response;
      if (invitacionForm.id) {
        response = await particularesApiService.updateAlumnoInvitacion(invitacionForm.id, payload);
        showSuccess('Invitación actualizada.');
        setModalType(null);
      } else {
        response = await particularesApiService.createAlumnoInvitacion(payload);
        const invitacion = response?.invitacion || response?.Invitacion;
        setInvitacionShare(invitacion ? { ...invitacion, link: buildInvitationLink(invitacion) } : null);
        setModalType('invitacionShare');
        showSuccess('Invitación creada.');
      }
      setInvitacionForm(emptyInvitacionForm);
      refreshAfterMutation();
    } catch (error) {
      showError(error, 'No pudimos guardar la invitación.');
    } finally {
      setSaving(false);
    }
  };

  const toggleInvitacionActivo = async (invitacion, activo) => {
    const id = getId(invitacion);
    if (!id || !puedeGestionarInvitaciones) return;
    setSaving(true);
    try {
      await particularesApiService.updateAlumnoInvitacionActivo(id, activo);
      showSuccess(activo ? 'Invitación activada.' : 'Invitación deshabilitada.');
      refreshAfterMutation();
    } catch (error) {
      showError(error, activo ? 'No pudimos activar la invitación.' : 'No pudimos deshabilitar la invitación.');
    } finally {
      setSaving(false);
    }
  };

  const saveCuentaCorrienteMovimiento = async (event) => {
    event.preventDefault();
    if (!puedeGestionarCuentaCorriente) {
      showError({ message: 'No tenés permisos para administrar cuenta corriente.' }, 'No tenés permisos para administrar cuenta corriente.');
      return;
    }

    if (cuentaCorrienteForm.tipoEntidad !== 'Empresa' && !cuentaCorrienteForm.idUsuarioEntidad) {
      showError({ message: 'Seleccioná un alumno o profesor.' }, 'Seleccioná un alumno o profesor.');
      return;
    }

    const importe = parseCuentaCorrienteAmountInput(cuentaCorrienteForm.importe);
    if (importe <= 0) {
      showError({ message: 'El importe debe ser mayor a cero.' }, 'El importe debe ser mayor a cero.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        tipoMovimiento: cuentaCorrienteForm.tipoMovimiento,
        tipoEntidad: cuentaCorrienteForm.tipoEntidad,
        idUsuarioEntidad: cuentaCorrienteForm.tipoEntidad === 'Empresa' ? null : cuentaCorrienteForm.idUsuarioEntidad,
        concepto: cuentaCorrienteForm.concepto,
        notas: cuentaCorrienteForm.notas,
        importe,
        fechaMovimiento: cuentaCorrienteForm.fechaMovimiento
      };

      if (cuentaCorrienteForm.id) {
        await particularesApiService.updateCuentaCorrienteMovimiento(cuentaCorrienteForm.id, payload);
        showSuccess('Movimiento actualizado.');
      } else {
        await particularesApiService.createCuentaCorrienteMovimiento(payload);
        showSuccess('Movimiento creado.');
      }

      setModalType(null);
      setCuentaCorrienteForm(emptyCuentaCorrienteForm);
      loadCuentaCorriente();
    } catch (error) {
      showError(error, 'No pudimos guardar el movimiento.');
    } finally {
      setSaving(false);
    }
  };

  const deleteCuentaCorrienteMovimiento = async (movimiento) => {
    const id = getId(movimiento);
    if (!id || !puedeGestionarCuentaCorriente) return;
    setSaving(true);
    try {
      await particularesApiService.deleteCuentaCorrienteMovimiento(id);
      showSuccess('Movimiento eliminado.');
      loadCuentaCorriente();
    } catch (error) {
      showError(error, 'No pudimos eliminar el movimiento.');
    } finally {
      setSaving(false);
    }
  };

  const toggleAlumnoActivo = async (alumno) => {
    const id = getId(alumno);
    if (!id) return;
    setSaving(true);
    try {
      await particularesApiService.updateAlumnoActivo(id, !getBool(alumno, 'activo', 'Activo', true));
      showSuccess('Estado del alumno actualizado.');
      refreshAfterMutation();
    } catch (error) {
      showError(error, 'No pudimos actualizar el alumno.');
    } finally {
      setSaving(false);
    }
  };

  const resetAlumnoPassword = async () => {
    const { target } = resetPasswordModal;
    const id = getId(target);
    if (!id) return;
    const resetInfo = getResetPasswordInfo('alumno', target);
    setSaving(true);
    try {
      await particularesApiService.resetAlumnoPassword(id);
      setModalType(null);
      setResetPasswordModal({ type: '', target: null });
      showSuccess(`Contraseña reseteada. Nuevo password: ${resetInfo.newPassword}.`);
    } catch (error) {
      setModalType(null);
      setResetPasswordModal({ type: '', target: null });
      showError(error, 'No pudimos resetear la contraseña.');
    } finally {
      setSaving(false);
    }
  };

  const confirmResetPassword = () => {
    if (resetPasswordModal.type === 'profesor') {
      resetProfesorAsociadoPassword();
      return;
    }

    if (resetPasswordModal.type === 'alumno') {
      resetAlumnoPassword();
    }
  };

  const saveCurso = async (event) => {
    event.preventDefault();
    if (!puedeGestionarCatalogo) {
      showError({ message: 'No tenés permisos para administrar cursos.' }, 'No tenés permisos para administrar cursos.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nombre: cursoForm.nombre,
        descripcion: cursoForm.descripcion,
        etiquetaIds: cursoForm.etiquetaIds,
        profesorIds: cursoForm.profesorIds,
        alumnoIds: cursoForm.alumnoIds
      };
      const successMessage = cursoForm.id ? 'Curso actualizado.' : 'Curso creado.';
      if (cursoForm.id) {
        await particularesApiService.updateCurso(cursoForm.id, payload);
      } else {
        await particularesApiService.createCurso(payload);
      }
      setCursoForm(emptyCursoForm);
      setModalType(null);
      showSuccess(successMessage);
      refreshAfterMutation();
    } catch (error) {
      showError(error, 'No pudimos guardar el curso.');
    } finally {
      setSaving(false);
    }
  };

  const deleteCursoExpectedText = useMemo(() => (
    cursoToDelete ? `ELIMINAR ${getName(cursoToDelete, '')}` : ''
  ), [cursoToDelete]);

  const canConfirmDeleteCurso = useMemo(() => (
    Boolean(cursoToDelete)
    && normalizeConfirmText(deleteCursoConfirmText) === normalizeConfirmText(deleteCursoExpectedText)
  ), [cursoToDelete, deleteCursoConfirmText, deleteCursoExpectedText]);

  const handleConfirmDeleteCurso = async () => {
    const cursoId = getId(cursoToDelete);
    if (!cursoId || !canConfirmDeleteCurso) return;
    if (!puedeGestionarCatalogo) {
      setDeleteCursoError('No tenés permisos para administrar cursos.');
      return;
    }

    setSaving(true);
    setDeleteCursoError('');
    try {
      await particularesApiService.deleteCurso(cursoId);
      showSuccess(`El curso ${getName(cursoToDelete, '')} fue eliminado junto con sus asociaciones.`);
      setModalType(null);
      setCursoToDelete(null);
      setDeleteCursoConfirmText('');
      refreshAfterMutation();
    } catch (error) {
      setDeleteCursoError(error.message || 'No pudimos eliminar el curso.');
    } finally {
      setSaving(false);
    }
  };

  const removeCursoMember = async (type, member) => {
    const curso = courseDetailModal.curso;
    const cursoId = getId(curso);
    const memberId = getId(member);
    if (!cursoId || !memberId) return;
    if (!puedeGestionarCatalogo) {
      showError({ message: 'No tenés permisos para modificar cursos.' }, 'No tenés permisos para modificar cursos.');
      return;
    }

    const isProfesor = type === 'profesores';
    const relationLowerKey = isProfesor ? 'profesores' : 'alumnos';
    const relationUpperKey = isProfesor ? 'Profesores' : 'Alumnos';
    const currentMembers = getArray(curso?.[relationLowerKey] || curso?.[relationUpperKey]);
    const nextMembers = currentMembers.filter((item) => getId(item) !== memberId);
    const nextIds = nextMembers.map(getId).filter(Boolean);
    const removingKey = `${type}:${memberId}`;

    setSaving(true);
    setCourseDetailRemovingKey(removingKey);
    try {
      const response = isProfesor
        ? await particularesApiService.setCursoProfesores(cursoId, nextIds)
        : await particularesApiService.setCursoAlumnos(cursoId, nextIds);
      const updatedCurso = response?.curso || response?.Curso || {
        ...curso,
        [relationLowerKey]: nextMembers,
        [relationUpperKey]: nextMembers
      };

      setCursos((prev) => prev.map((item) => (getId(item) === cursoId ? updatedCurso : item)));
      setCourseDetailModal((prev) => ({
        ...prev,
        curso: getId(prev.curso) === cursoId ? updatedCurso : prev.curso
      }));
      showSuccess(isProfesor ? 'Profesor desasociado del curso.' : 'Alumno desasociado del curso.');
      refreshAfterMutation();
    } catch (error) {
      showError(error, isProfesor ? 'No pudimos desasociar el profesor.' : 'No pudimos desasociar el alumno.');
    } finally {
      setSaving(false);
      setCourseDetailRemovingKey('');
    }
  };

  const saveAula = async (event) => {
    event.preventDefault();
    if (!aulaForm.idCursoParticular) {
      showError({ message: 'Seleccioná un curso para el aula.' }, 'Seleccioná un curso para el aula.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        idCursoParticular: aulaForm.idCursoParticular,
        nombre: aulaForm.nombre,
        descripcion: aulaForm.descripcion,
        aulaPublica: false,
        mostrarArchivos: aulaForm.mostrarArchivos,
        mostrarLinks: aulaForm.mostrarLinks,
        mostrarPizarra: aulaForm.mostrarPizarra,
        mostrarChat: aulaForm.mostrarChat,
        mostrarVideollamada: aulaForm.mostrarVideollamada,
        mostrarForo: aulaForm.mostrarForo,
        mostrarAsistentesIA: aulaForm.mostrarAsistentesIA
      };
      const successMessage = aulaForm.id ? 'Aula actualizada.' : 'Aula creada.';
      if (aulaForm.id) {
        await particularesApiService.updateAula(aulaForm.id, payload);
      } else {
        await particularesApiService.createAula(payload);
      }
      setAulaForm(emptyAulaForm);
      setModalType(null);
      showSuccess(successMessage);
      refreshAfterMutation();
    } catch (error) {
      showError(error, 'No pudimos guardar el aula.');
    } finally {
      setSaving(false);
    }
  };

  const deleteAula = async (aula) => {
    const id = getId(aula);
    if (!id) return;
    setSaving(true);
    try {
      await particularesApiService.deleteAula(id);
      showSuccess('Aula eliminada.');
      refreshAfterMutation();
    } catch (error) {
      showError(error, 'No pudimos eliminar el aula.');
    } finally {
      setSaving(false);
    }
  };

  const saveAulaAssistants = async (event) => {
    event.preventDefault();
    if (!selectedAulaAssistantId) return;
    setSaving(true);
    try {
      await particularesApiService.setAulaAsistentes(selectedAulaAssistantId, assignedAssistantIds);
      showSuccess('Asistentes asignados al aula.');
      setModalType(null);
      refreshAfterMutation();
    } catch (error) {
      showError(error, 'No pudimos asignar asistentes.');
    } finally {
      setSaving(false);
    }
  };

  const openAula = (aula) => {
    const id = getId(aula);
    if (!id) return;
    navigate(`/particular/aula/${encodeURIComponent(id)}`);
  };

  const renderCursosTable = (source = cursos, emptyText = 'No hay cursos creados.', gridKey = 'cursos') => {
    const state = gridState[gridKey] || createGridState();
    const grid = getPagedGrid(source, state, getCursoSearchText);
    const emptyMessage = state.search ? 'No hay resultados para la búsqueda.' : emptyText;

    return (
      <>
        <GridToolbar
          state={state}
          grid={grid}
          placeholder="Buscar por curso, etiqueta, profesor o alumno"
          onSearchChange={(search) => updateGridSearch(gridKey, search)}
          onPageSizeChange={(pageSize) => updateGridPageSize(gridKey, pageSize)}
          exportColumns={cursoExportColumns}
          exportRows={grid.filteredItems}
          exportFileName={`${gridKey === 'resumenCursos' ? 'cursos-activos' : 'cursos'}-${particularName || 'particular'}`}
          exportTitle={`${gridKey === 'resumenCursos' ? 'Cursos activos' : 'Cursos'} - ${particularName || 'Particular'}`}
        />
        <div className={tableWrapperClassName}>
          <table className="particular-table">
            <thead>
              <tr>
                <th>Curso</th>
                <th>Etiquetas</th>
                <th>Profesores</th>
                <th>Alumnos</th>
                <th>Aulas</th>
                <th className="actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {grid.items.map((curso) => {
                const cursoProfesores = getArray(curso?.profesores || curso?.Profesores);
                const cursoAlumnos = getArray(curso?.alumnos || curso?.Alumnos);
                const cursoAulas = getCursoAulas(curso);
                const cantidadAulas = cursoAulas.length || Number(curso?.cantidadAulas ?? curso?.CantidadAulas ?? 0);

                return (
                  <tr key={getId(curso)}>
                    <td>
                      <strong>{getName(curso)}</strong>
                      <span>{getDescripcion(curso) || 'Sin descripción'}</span>
                    </td>
                    <td>
                      <div className="particular-tags table-tags">
                        {(curso?.etiquetas || curso?.Etiquetas || []).map((etiqueta) => <TagPill key={getId(etiqueta)} etiqueta={etiqueta} />)}
                      </div>
                    </td>
                    <td>
                      <CourseCountButton
                        value={cursoProfesores.length}
                        title="Ver profesores del curso"
                        onClick={() => openCursoDetailModal('profesores', curso)}
                      />
                    </td>
                    <td>
                      <CourseCountButton
                        value={cursoAlumnos.length}
                        title="Ver alumnos del curso"
                        onClick={() => openCursoDetailModal('alumnos', curso)}
                      />
                    </td>
                    <td>
                      <CourseCountButton
                        value={cantidadAulas}
                        title="Ver aulas del curso"
                        onClick={() => openCursoDetailModal('aulas', curso)}
                      />
                    </td>
                    <td className="actions">
                      {puedeGestionarCatalogo ? (
                        <>
                          <button type="button" className="particular-icon-btn action-edit" onClick={() => openCursoModal(curso)} title="Editar">
                            <i className="fas fa-pen" />
                          </button>
                          <button type="button" className="particular-icon-btn danger" onClick={() => openDeleteCursoModal(curso)} title="Eliminar">
                            <i className="fas fa-trash" />
                          </button>
                        </>
                      ) : (
                        <span className="particular-action-muted">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {grid.items.length === 0 && <TableEmpty colSpan={6} text={emptyMessage} />}
            </tbody>
          </table>
          {isRefreshing && <GridLoadingOverlay />}
        </div>
        <GridPagination grid={grid} onPageChange={(page) => updateGridPage(gridKey, page)} />
      </>
    );
  };

  const renderResumen = () => {
    const data = dashboardData || {};
    const resumen = data.resumen || data.Resumen || {};
    const series = data.series || data.Series || {};
    const rankings = data.rankings || data.Rankings || {};
    const actividad = data.actividad || data.Actividad || {};
    const dashboardParticular = data.particular || data.Particular || {};
    const scope = data.scope || data.Scope || {};
    const duenio = dashboardParticular.duenio || dashboardParticular.Duenio || {};
    const actividad7Dias = getDashboardArray(series, 'actividad7Dias', 'Actividad7Dias');
    const actividadPorTipo = getDashboardArray(series, 'actividadPorTipo', 'ActividadPorTipo');
    const hoyPorHora = getDashboardArray(actividad, 'hoyPorHora', 'HoyPorHora');
    const reciente = getDashboardArray(actividad, 'reciente', 'Reciente');
    const topProfesores = getDashboardArray(rankings, 'topProfesores', 'TopProfesores');
    const topAlumnos = getDashboardArray(rankings, 'topAlumnos', 'TopAlumnos');
    const topCursos = getDashboardArray(rankings, 'topCursos', 'TopCursos');
    const topAulas = getDashboardArray(rankings, 'topAulas', 'TopAulas');
    const categorias = getDashboardArray(rankings, 'categoriasMasUsadas', 'CategoriasMasUsadas');
    const topInvitacionesRaw = getDashboardArray(rankings, 'topInvitaciones', 'TopInvitaciones');
    const telemetriaDisponible = getDashboardProp(resumen, 'telemetriaDisponible', 'TelemetriaDisponible') !== false;
    const limitadoAProfesor = Boolean(getDashboardProp(scope, 'limitadoAProfesor', 'LimitadoAProfesor'));
    const totalCursos = getDashboardProp(resumen, 'totalCursos', 'TotalCursos') ?? stats.cursos;
    const totalAulas = getDashboardProp(resumen, 'totalAulas', 'TotalAulas') ?? stats.aulas;
    const totalProfesores = getDashboardProp(resumen, 'totalProfesores', 'TotalProfesores') ?? stats.profesores;
    const totalAlumnos = getDashboardProp(resumen, 'totalAlumnos', 'TotalAlumnos') ?? stats.alumnos;
    const totalAsistentes = getDashboardProp(resumen, 'totalAsistentes', 'TotalAsistentes') ?? stats.asistentes;
    const actividadHoy = getDashboardProp(resumen, 'actividadHoy', 'ActividadHoy') ?? 0;
    const chatsHoy = getDashboardProp(resumen, 'chatsHoy', 'ChatsHoy') ?? 0;
    const pizarrasHoy = getDashboardProp(resumen, 'pizarrasHoy', 'PizarrasHoy') ?? 0;
    const videollamadasHoy = getDashboardProp(resumen, 'videollamadasHoy', 'VideollamadasHoy') ?? 0;
    const asistentesHoy = getDashboardProp(resumen, 'asistentesHoy', 'AsistentesHoy') ?? 0;
    const totalArchivos = getDashboardProp(resumen, 'totalArchivos', 'TotalArchivos') ?? 0;
    const almacenamientoArchivosBytes = getDashboardProp(resumen, 'almacenamientoArchivosBytes', 'AlmacenamientoArchivosBytes') ?? 0;
    const alumnosConActividadHoy = getDashboardProp(resumen, 'alumnosConActividadHoy', 'AlumnosConActividadHoy') ?? 0;
    const promedioAlumnos = getDashboardProp(resumen, 'promedioAlumnosPorCurso', 'PromedioAlumnosPorCurso') ?? 0;
    const aulasConAsistentesPct = getDashboardProp(resumen, 'aulasConAsistentesPct', 'AulasConAsistentesPct') ?? 0;
    const puedeVerInvitacionesDashboard = puedeGestionarInvitaciones || Boolean(getDashboardProp(resumen, 'puedeGestionarInvitaciones', 'PuedeGestionarInvitaciones'));
    const totalInvitaciones = getDashboardProp(resumen, 'totalInvitaciones', 'TotalInvitaciones') ?? invitationStats.total;
    const invitacionesActivas = getDashboardProp(resumen, 'invitacionesActivas', 'InvitacionesActivas') ?? invitationStats.activas;
    const invitacionesCaducadas = getDashboardProp(resumen, 'invitacionesCaducadas', 'InvitacionesCaducadas') ?? invitationStats.caducadas;
    const invitacionesInactivas = getDashboardProp(resumen, 'invitacionesInactivas', 'InvitacionesInactivas') ?? invitationStats.inactivas;
    const alumnosRegistradosPorInvitacion = getDashboardProp(resumen, 'alumnosRegistradosPorInvitacion', 'AlumnosRegistradosPorInvitacion') ?? invitationStats.registros;
    const invitacionesSinRegistros = getDashboardProp(resumen, 'invitacionesSinRegistros', 'InvitacionesSinRegistros') ?? invitationStats.sinRegistros;
    const topInvitaciones = (topInvitacionesRaw.length ? topInvitacionesRaw : invitationStats.top).map((item) => ({
      id: item.id || item.Id || item.codigo || item.Codigo,
      nombre: item.nombre || item.Nombre || item.codigo || item.Codigo || 'Sin código',
      codigo: item.codigo || item.Codigo || item.nombre || item.Nombre || '',
      estado: item.estado || item.Estado || '-',
      fechaCaducidadUtc: item.fechaCaducidadUtc || item.FechaCaducidadUtc || '',
      registros: Number(item.registros ?? item.Registros ?? item.cantidadRegistros ?? item.CantidadRegistros ?? 0) || 0
    }));
    const heroPersonName = !isGeneralAdmin && isProfesorParticularRole
      ? dashboardHeaderName
      : (duenio?.nombre || duenio?.Nombre || 'Dueño de cuenta');

    return (
      <div className="particular-dashboard-home">
        <section className="particular-dashboard-hero">
          <div className="particular-dashboard-hero-content">
            <span className="particular-dashboard-eyebrow">Panel ejecutivo</span>
            <h2>{dashboardHeaderName}</h2>
            <p>{limitadoAProfesor ? 'Vista operativa de tus cursos asignados.' : 'Vista completa de la cuenta particular.'}</p>
            <div className="particular-dashboard-hero-meta">
              <span>
                <i className="fas fa-user-shield" />
                {heroPersonName}
              </span>
              <span>
                <i className="fas fa-bolt" />
                {formatDashboardNumber(actividadHoy)} eventos hoy
              </span>
              <span>
                <i className="fas fa-robot" />
                {formatDashboardNumber(asistentesHoy)} usos IA hoy
              </span>
            </div>
          </div>
          <div className="particular-dashboard-hero-side">
            <DashboardIllustration />
            <button type="button" className="particular-secondary-btn" onClick={loadDashboard} disabled={dashboardLoading}>
              <i className={`fas ${dashboardLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
              Actualizar tablero
            </button>
          </div>
        </section>

        {dashboardLoading && (
          <div className="particular-dashboard-loading">
            <LoadingSpinner message="Actualizando dashboard..." size="small" inline />
          </div>
        )}

        {dashboardError && (
          <div className="particular-dashboard-warning">
            <i className="fas fa-exclamation-triangle" />
            <span>{dashboardError}</span>
          </div>
        )}

        {!telemetriaDisponible && (
          <div className="particular-dashboard-warning">
            <i className="fas fa-database" />
            <span>La telemetría histórica todavía no está disponible para este entorno.</span>
          </div>
        )}

        <section className="particular-dashboard-kpis">
          <DashboardKpiCard icon="fas fa-layer-group" label="Cursos activos" value={totalCursos} detail={`${formatDashboardDecimal(promedioAlumnos)} alumnos promedio`} tone="blue" />
          <DashboardKpiCard icon="fas fa-door-open" label="Aulas" value={totalAulas} detail={`${formatDashboardNumber(aulasConAsistentesPct)}% con asistentes`} tone="cyan" />
          <DashboardKpiCard icon="fas fa-user-tie" label="Profesores" value={totalProfesores} detail={limitadoAProfesor ? 'Cursos asignados' : 'Equipo activo'} tone="violet" />
          <DashboardKpiCard icon="fas fa-user-graduate" label="Alumnos" value={totalAlumnos} detail={`${formatDashboardNumber(alumnosConActividadHoy)} activos hoy`} tone="green" />
          {puedeVerInvitacionesDashboard && (
            <>
              <DashboardKpiCard icon="fas fa-ticket-alt" label="Invitaciones" value={totalInvitaciones} detail={`${formatDashboardNumber(invitacionesActivas)} activas`} tone="amber" />
              <DashboardKpiCard icon="fas fa-user-plus" label="Altas invitadas" value={alumnosRegistradosPorInvitacion} detail={`${formatDashboardNumber(invitacionesSinRegistros)} sin uso`} tone="teal" />
            </>
          )}
          <DashboardKpiCard icon="fas fa-robot" label="Asistentes IA" value={totalAsistentes} detail={`${formatDashboardNumber(asistentesHoy)} usos hoy`} tone="purple" />
          <DashboardKpiCard icon="fas fa-comments" label="Chats hoy" value={chatsHoy} detail="Chat grupal" tone="teal" />
          <DashboardKpiCard icon="fas fa-chalkboard" label="Pizarra" value={pizarrasHoy} detail="Actividad de hoy" tone="amber" />
          <DashboardKpiCard icon="fas fa-video" label="Videollamadas" value={videollamadasHoy} detail="Actividad de hoy" tone="red" />
          <DashboardKpiCard
            icon="fas fa-database"
            label="Almacenamiento"
            value={almacenamientoArchivosBytes}
            displayValue={formatStorageGigabytes(almacenamientoArchivosBytes)}
            detail={`${formatDashboardNumber(totalArchivos)} archivos subidos`}
            tone="storage"
            className="wide"
          />
        </section>

        <div className="particular-dashboard-grid main">
          <ChartCard icon="fas fa-chart-line" title="Actividad semanal" subtitle="Eventos por día">
            <LineChart data={actividad7Dias} valueKey="total" />
          </ChartCard>
          <ChartCard icon="fas fa-chart-pie" title="Distribución de uso" subtitle="Eventos agrupados por canal">
            <DonutChart data={actividadPorTipo} />
          </ChartCard>
        </div>

        <div className="particular-dashboard-grid three">
          <ChartCard icon="fas fa-chart-bar" title="Actividad por canal" subtitle="Últimos 30 días">
            <BarListChart data={actividadPorTipo} />
          </ChartCard>
          <ChartCard icon="fas fa-clock" title="Actividad del día" subtitle="Eventos por hora">
            <HourlyActivityChart data={hoyPorHora} />
          </ChartCard>
          <ChartCard icon="fas fa-boxes" title="Recursos" subtitle="Contenido disponible">
            <div className="particular-resource-metrics">
              <article>
                <i className="fas fa-file" />
                <span>Archivos</span>
                <strong>{formatDashboardNumber(totalArchivos)}</strong>
              </article>
              <article>
                <i className="fas fa-comment-alt" />
                <span>Mensajes</span>
                <strong>{formatDashboardNumber(getDashboardProp(resumen, 'totalMensajesForo', 'TotalMensajesForo') ?? 0)}</strong>
              </article>
              <article>
                <i className="fas fa-reply" />
                <span>Respuestas</span>
                <strong>{formatDashboardNumber(getDashboardProp(resumen, 'totalRespuestasForo', 'TotalRespuestasForo') ?? 0)}</strong>
              </article>
              <article>
                <i className="fas fa-tags" />
                <span>Etiquetas</span>
                <strong>{formatDashboardNumber(getDashboardProp(resumen, 'totalEtiquetas', 'TotalEtiquetas') ?? stats.etiquetas)}</strong>
              </article>
            </div>
          </ChartCard>
        </div>

        {puedeVerInvitacionesDashboard && (
          <div className="particular-dashboard-grid two">
            <ChartCard icon="fas fa-ticket-alt" title="Invitaciones" subtitle="Estado de códigos de alta">
              <div className="particular-resource-metrics">
                <article>
                  <i className="fas fa-check-circle" />
                  <span>Activas</span>
                  <strong>{formatDashboardNumber(invitacionesActivas)}</strong>
                </article>
                <article>
                  <i className="fas fa-user-plus" />
                  <span>Registrados</span>
                  <strong>{formatDashboardNumber(alumnosRegistradosPorInvitacion)}</strong>
                </article>
                <article>
                  <i className="fas fa-clock" />
                  <span>Caducadas</span>
                  <strong>{formatDashboardNumber(invitacionesCaducadas)}</strong>
                </article>
                <article>
                  <i className="fas fa-ban" />
                  <span>Inactivas</span>
                  <strong>{formatDashboardNumber(invitacionesInactivas)}</strong>
                </article>
              </div>
            </ChartCard>
            <ChartCard icon="fas fa-list-ol" title="Invitaciones con más altas" subtitle="Alumnos registrados por código">
              <RankingList
                items={topInvitaciones}
                metricKey="registros"
                metricLabel="altas"
                emptyText="Todavía no hay invitaciones creadas."
                secondary={(item) => `${item.estado || '-'} · vence ${formatDateOnly(item.fechaCaducidadUtc)}`}
              />
            </ChartCard>
          </div>
        )}

        <div className="particular-dashboard-grid two">
          <ChartCard icon="fas fa-trophy" title="Top profesores" subtitle="Por actividad registrada">
            <RankingList
              items={topProfesores}
              metricKey="actividad"
              metricLabel="eventos"
              emptyText="Todavía no hay profesores con actividad."
              secondary={(item) => `${formatDashboardNumber(item.cursos)} cursos, ${formatDashboardNumber(item.alumnos)} alumnos`}
            />
          </ChartCard>
          <ChartCard icon="fas fa-user-graduate" title="Top alumnos" subtitle="Participación y uso de IA">
            <RankingList
              items={topAlumnos}
              metricKey="actividad"
              metricLabel="eventos"
              emptyText="Todavía no hay alumnos con actividad."
              secondary={(item) => `${formatDashboardNumber(item.cursos)} cursos, ${formatDashboardNumber(item.asistentes)} IA`}
            />
          </ChartCard>
        </div>

        <div className="particular-dashboard-grid two">
          <ChartCard icon="fas fa-layer-group" title="Cursos con más alumnos" subtitle="Matrícula y actividad">
            <RankingList
              items={topCursos}
              metricKey="alumnos"
              metricLabel="alumnos"
              emptyText="Todavía no hay cursos para rankear."
              secondary={(item) => `${formatDashboardNumber(item.aulas)} aulas, ${formatDashboardNumber(item.actividad)} eventos`}
            />
          </ChartCard>
          <ChartCard icon="fas fa-tags" title="Categorías más usadas" subtitle="Etiquetas aplicadas a cursos">
            <RankingList
              items={categorias}
              metricKey="cursos"
              metricLabel="cursos"
              emptyText="Todavía no hay etiquetas asociadas a cursos."
              colorKey="color"
              secondary={(item) => `${formatDashboardNumber(item.alumnos)} alumnos, ${formatDashboardNumber(item.aulas)} aulas`}
            />
          </ChartCard>
        </div>

        <div className="particular-dashboard-grid two">
          <ChartCard icon="fas fa-door-open" title="Aulas más activas" subtitle="Interacciones y recursos">
            <RankingList
              items={topAulas}
              metricKey="actividad"
              metricLabel="eventos"
              emptyText="Todavía no hay aulas con actividad."
              secondary={(item) => `${item.curso || 'Curso'} · ${formatDashboardNumber(item.alumnos)} alumnos · ${formatDashboardNumber(item.asistentes)} IA`}
            />
          </ChartCard>
          <ChartCard icon="fas fa-bell" title="Actividad reciente" subtitle="Últimos movimientos">
            {reciente.length === 0 ? (
              <ChartEmpty text="Todavía no hay movimientos recientes." />
            ) : (
              <div className="particular-activity-feed">
                {reciente.map((item, index) => (
                  <article key={`${item.fechaUtc || item.FechaUtc || index}-${index}`}>
                    <div className="particular-activity-icon">
                      <i className="fas fa-bolt" />
                    </div>
                    <div>
                      <strong>{item.evento || item.Evento || 'Actividad'}</strong>
                      <span>{item.detalle || item.Detalle || item.aula || item.Aula || '-'}</span>
                      <small>
                        {item.actor || item.Actor || 'Usuario'}
                        {(item.aula || item.Aula) ? ` · ${item.aula || item.Aula}` : ''}
                      </small>
                    </div>
                    <time>{formatDashboardDateTime(item.fechaUtc || item.FechaUtc || item.fechaLocal || item.FechaLocal)}</time>
                  </article>
                ))}
              </div>
            )}
          </ChartCard>
        </div>

        <section className="content-card particular-section particular-dashboard-table-section">
          <SectionHeader
            icon="fas fa-layer-group"
            title="Cursos activos"
            subtitle={`${cursos.length} cursos cargados`}
            onNew={puedeGestionarCatalogo ? () => openCursoModal() : null}
          />
          <div className="card-content">
            {renderCursosTable(cursos, 'No hay cursos creados.', 'resumenCursos')}
          </div>
        </section>
      </div>
    );
  };

  const renderEtiquetas = () => {
    const state = gridState.etiquetas || createGridState();
    const grid = getPagedGrid(etiquetas, state, getEtiquetaSearchText);

    return (
      <section className="content-card particular-section">
        <SectionHeader
          icon="fas fa-tags"
          title="Etiquetas"
          subtitle={`${etiquetas.length} etiquetas cargadas`}
          onNew={puedeGestionarCatalogo ? () => openEtiquetaModal() : null}
        />
        <div className="card-content">
          <GridToolbar
            state={state}
            grid={grid}
            placeholder="Buscar por nombre, descripción o color"
            onSearchChange={(search) => updateGridSearch('etiquetas', search)}
            onPageSizeChange={(pageSize) => updateGridPageSize('etiquetas', pageSize)}
            exportColumns={etiquetaExportColumns}
            exportRows={grid.filteredItems}
            exportFileName={`etiquetas-${particularName || 'particular'}`}
            exportTitle={`Etiquetas - ${particularName || 'Particular'}`}
          />
          <div className={tableWrapperClassName}>
            <table className="particular-table">
              <thead>
                <tr>
                  <th>Color</th>
                  <th>Nombre</th>
                  <th>Descripción</th>
                  <th>Estado</th>
                  <th className="actions">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {grid.items.map((etiqueta) => (
                  <tr key={getId(etiqueta)}>
                    <td><span className="particular-color-dot" style={{ backgroundColor: etiqueta?.color || etiqueta?.Color || '#2563eb' }} /></td>
                    <td><TagPill etiqueta={etiqueta} /></td>
                    <td>{getDescripcion(etiqueta) || '-'}</td>
                    <td><StatusPill active={getBool(etiqueta, 'activo', 'Activo', true)} /></td>
                    <td className="actions">
                      {puedeGestionarCatalogo ? (
                        <>
                          <button type="button" className="particular-icon-btn action-edit" onClick={() => openEtiquetaModal(etiqueta)} title="Editar">
                            <i className="fas fa-pen" />
                          </button>
                          <button type="button" className="particular-icon-btn danger" onClick={() => deleteEtiqueta(etiqueta)} title="Eliminar">
                            <i className="fas fa-trash" />
                          </button>
                        </>
                      ) : (
                        <span className="particular-action-muted">-</span>
                      )}
                    </td>
                  </tr>
                ))}
                {grid.items.length === 0 && (
                  <TableEmpty colSpan={5} text={state.search ? 'No hay resultados para la búsqueda.' : 'No hay etiquetas creadas.'} />
                )}
              </tbody>
            </table>
            {isRefreshing && <GridLoadingOverlay />}
          </div>
          <GridPagination grid={grid} onPageChange={(page) => updateGridPage('etiquetas', page)} />
        </div>
      </section>
    );
  };

  const renderProfesoresAsociados = () => {
    const state = gridState.profesoresAsociados || createGridState();
    const grid = getPagedGrid(profesoresAsociados, state, getProfesorAsociadoSearchText);

    return (
      <section className="content-card particular-section">
        <SectionHeader
          icon="fas fa-user-tie"
          title="Profesores particulares"
          subtitle={`${profesoresAsociados.length} profesores cargados`}
          onNew={() => openProfesorAsociadoModal()}
        />
        <div className="card-content">
          <GridToolbar
            state={state}
            grid={grid}
            placeholder="Buscar por profesor, email, DNI o teléfono"
            onSearchChange={(search) => updateGridSearch('profesoresAsociados', search)}
            onPageSizeChange={(pageSize) => updateGridPageSize('profesoresAsociados', pageSize)}
            exportColumns={profesorExportColumns}
            exportRows={grid.filteredItems}
            exportFileName={`profesores-particulares-${particularName || 'particular'}`}
            exportTitle={`Profesores particulares - ${particularName || 'Particular'}`}
          />
          <div className={tableWrapperClassName}>
            <table className="particular-table">
              <thead>
                <tr>
                  <th>Profesor</th>
                  <th>Email</th>
                  <th>DNI</th>
                  <th>Teléfono</th>
                  <th>Estado</th>
                  <th className="actions">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {grid.items.map((profesorAsociado) => {
                  const activo = getBool(profesorAsociado, 'activo', 'Activo', true);
                  return (
                    <tr key={getId(profesorAsociado)} className={activo ? '' : 'row-muted'}>
                      <td><strong>{getFullName(profesorAsociado, getEmail(profesorAsociado))}</strong></td>
                      <td>{getEmail(profesorAsociado)}</td>
                      <td>{profesorAsociado?.dni || profesorAsociado?.Dni || '-'}</td>
                      <td>{profesorAsociado?.telefono || profesorAsociado?.Telefono || '-'}</td>
                      <td><StatusPill active={activo} /></td>
                      <td className="actions">
                        <button type="button" className="particular-icon-btn action-edit" onClick={() => openProfesorAsociadoModal(profesorAsociado)} title="Editar">
                          <i className="fas fa-pen" />
                        </button>
                        <button
                          type="button"
                          className="particular-icon-btn reset"
                          onClick={() => openResetPasswordModal('profesor', profesorAsociado)}
                          disabled={saving}
                          title="Resetear contraseña"
                        >
                          <i className="fas fa-key" />
                        </button>
                        <button type="button" className={`particular-icon-btn ${activo ? 'danger' : 'success'}`} onClick={() => toggleProfesorAsociadoActivo(profesorAsociado)} title={activo ? 'Desactivar' : 'Activar'}>
                          <i className={`fas ${activo ? 'fa-ban' : 'fa-check'}`} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {grid.items.length === 0 && (
                  <TableEmpty colSpan={6} text={state.search ? 'No hay resultados para la búsqueda.' : 'No hay profesores creados.'} />
                )}
              </tbody>
            </table>
            {isRefreshing && <GridLoadingOverlay />}
          </div>
          <GridPagination grid={grid} onPageChange={(page) => updateGridPage('profesoresAsociados', page)} />
        </div>
      </section>
    );
  };

  const renderAlumnos = () => {
    const state = gridState.alumnos || createGridState();
    const grid = getPagedGrid(alumnos, state, getAlumnoSearchText);

    return (
      <section className="content-card particular-section">
        <SectionHeader
          icon="fas fa-user-graduate"
          title="Alumnos"
          subtitle={`${alumnos.length} alumnos cargados`}
          onNew={() => openAlumnoModal()}
        >
          {puedeGestionarInvitaciones && (
            <button type="button" className="particular-secondary-btn" onClick={() => openInvitacionModal()}>
              <i className="fas fa-ticket-alt" />
              Invitar
            </button>
          )}
        </SectionHeader>
        <div className="card-content">
          <GridToolbar
            state={state}
            grid={grid}
            placeholder="Buscar por alumno, email, DNI o teléfono"
            onSearchChange={(search) => updateGridSearch('alumnos', search)}
            onPageSizeChange={(pageSize) => updateGridPageSize('alumnos', pageSize)}
            exportColumns={alumnoExportColumns}
            exportRows={grid.filteredItems}
            exportFileName={`alumnos-particulares-${particularName || 'particular'}`}
            exportTitle={`Alumnos particulares - ${particularName || 'Particular'}`}
          />
          <div className={tableWrapperClassName}>
            <table className="particular-table">
              <thead>
                <tr>
                  <th>Alumno</th>
                  <th>Email</th>
                  <th>DNI</th>
                  <th>Teléfono</th>
                  <th>Estado</th>
                  <th className="actions">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {grid.items.map((alumno) => {
                  const activo = getBool(alumno, 'activo', 'Activo', true);
                  return (
                    <tr key={getId(alumno)} className={activo ? '' : 'row-muted'}>
                      <td><strong>{getFullName(alumno, getEmail(alumno))}</strong></td>
                      <td>{getEmail(alumno)}</td>
                      <td>{alumno?.dni || alumno?.Dni || '-'}</td>
                      <td>{alumno?.telefono || alumno?.Telefono || '-'}</td>
                      <td><StatusPill active={activo} /></td>
                      <td className="actions">
                        <button type="button" className="particular-icon-btn action-edit" onClick={() => openAlumnoModal(alumno)} title="Editar">
                          <i className="fas fa-pen" />
                        </button>
                        <button
                          type="button"
                          className="particular-icon-btn reset"
                          onClick={() => openResetPasswordModal('alumno', alumno)}
                          disabled={saving}
                          title="Resetear contraseña"
                        >
                          <i className="fas fa-key" />
                        </button>
                        <button type="button" className={`particular-icon-btn ${activo ? 'danger' : 'success'}`} onClick={() => toggleAlumnoActivo(alumno)} title={activo ? 'Desactivar' : 'Activar'}>
                          <i className={`fas ${activo ? 'fa-ban' : 'fa-check'}`} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {grid.items.length === 0 && (
                  <TableEmpty colSpan={6} text={state.search ? 'No hay resultados para la búsqueda.' : 'No hay alumnos creados.'} />
                )}
              </tbody>
            </table>
            {isRefreshing && <GridLoadingOverlay />}
          </div>
          <GridPagination grid={grid} onPageChange={(page) => updateGridPage('alumnos', page)} />
        </div>
      </section>
    );
  };

  const renderInvitaciones = () => {
    const state = gridState.invitaciones || createGridState();
    const grid = getPagedGrid(invitaciones, state, getInvitationSearchText);

    return (
      <section className="content-card particular-section">
        <SectionHeader
          icon="fas fa-ticket-alt"
          title="Invitaciones"
          subtitle={`${invitaciones.length} invitaciones cargadas`}
          onNew={() => openInvitacionModal()}
          newLabel="Nueva invitación"
        />
        <div className="card-content">
          <GridToolbar
            state={state}
            grid={grid}
            placeholder="Buscar por código, estado o fecha"
            onSearchChange={(search) => updateGridSearch('invitaciones', search)}
            onPageSizeChange={(pageSize) => updateGridPageSize('invitaciones', pageSize)}
            exportColumns={invitacionExportColumns}
            exportRows={grid.filteredItems}
            exportFileName={`invitaciones-alumnos-${particularName || 'particular'}`}
            exportTitle={`Invitaciones de alumnos - ${particularName || 'Particular'}`}
          />
          <div className={tableWrapperClassName}>
            <table className="particular-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Caducidad</th>
                  <th>Estado</th>
                  <th>Registrados</th>
                  <th className="actions">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {grid.items.map((invitacion) => {
                  const estado = getInvitationStatus(invitacion);
                  const activa = estado.toLowerCase() === 'activa';
                  const activoFlag = getBool(invitacion, 'activo', 'Activo', true);
                  return (
                    <tr key={getId(invitacion)} className={activa ? '' : 'row-muted'}>
                      <td>
                        <strong className="particular-code-value">{getInvitationCode(invitacion)}</strong>
                      </td>
                      <td>{formatDateOnly(getInvitationExpiration(invitacion))}</td>
                      <td><StatusPill active={activa} trueText={estado} falseText={estado} /></td>
                      <td>
                        <CourseCountButton
                          value={getInvitationCount(invitacion)}
                          title="Ver alumnos registrados"
                          onClick={() => openInvitacionDetailModal(invitacion)}
                        />
                      </td>
                      <td className="actions">
                        <button type="button" className="particular-icon-btn primary" onClick={() => openInvitacionShareModal(invitacion)} title="Compartir">
                          <i className="fas fa-share-alt" />
                        </button>
                        <button type="button" className="particular-icon-btn view" onClick={() => openInvitacionDetailModal(invitacion)} title="Ver detalle">
                          <i className="fas fa-eye" />
                        </button>
                        <button type="button" className="particular-icon-btn action-edit" onClick={() => openInvitacionModal(invitacion)} title="Editar">
                          <i className="fas fa-pen" />
                        </button>
                        {activoFlag ? (
                          <button type="button" className="particular-icon-btn danger" onClick={() => toggleInvitacionActivo(invitacion, false)} disabled={saving} title="Deshabilitar">
                            <i className="fas fa-ban" />
                          </button>
                        ) : (
                          <button type="button" className="particular-icon-btn success" onClick={() => toggleInvitacionActivo(invitacion, true)} disabled={saving} title="Activar">
                            <i className="fas fa-check" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {grid.items.length === 0 && (
                  <TableEmpty colSpan={5} text={state.search ? 'No hay resultados para la búsqueda.' : 'No hay invitaciones creadas.'} />
                )}
              </tbody>
            </table>
            {isRefreshing && <GridLoadingOverlay />}
          </div>
          <GridPagination grid={grid} onPageChange={(page) => updateGridPage('invitaciones', page)} />
        </div>
      </section>
    );
  };

  const renderCuentaCorriente = () => {
    const pageSize = cuentaCorrienteFilters.pageSize || DEFAULT_GRID_PAGE_SIZE;
    const currentPage = Math.max(cuentaCorrienteFilters.page || 1, 1);
    const totalPages = Math.max(1, Math.ceil((cuentaCorrienteTotal || 0) / pageSize));
    const startItem = cuentaCorrienteTotal === 0 ? 0 : ((currentPage - 1) * pageSize) + 1;
    const endItem = Math.min(cuentaCorrienteTotal, currentPage * pageSize);
    const entityOptions = cuentaCorrienteFilters.tipoEntidad === 'Profesor'
      ? profesorSelectOptions
      : cuentaCorrienteFilters.tipoEntidad === 'Alumno'
        ? alumnoSelectOptions
        : [];
    const movementTypeOptions = [
      { value: 'todos', label: 'Todos' },
      { value: 'Credito', label: 'Créditos' },
      { value: 'Debito', label: 'Débitos' }
    ];
    const entityTypeOptions = [
      { value: 'todos', label: 'Todas' },
      { value: 'Alumno', label: 'Alumnos' },
      { value: 'Profesor', label: 'Profesores' },
      { value: 'Empresa', label: 'Empresa' }
    ];
    const pageGrid = {
      filteredTotal: cuentaCorrienteTotal,
      totalItems: cuentaCorrienteTotal,
      startItem,
      endItem,
      currentPage,
      totalPages
    };

    return (
      <section className="content-card particular-section">
        <SectionHeader
          icon="fas fa-file-invoice-dollar"
          title="Cuenta corriente"
          subtitle={`${formatDashboardNumber(cuentaCorrienteTotal)} movimientos`}
          onNew={() => openCuentaCorrienteMovimientoModal(null, cuentaCorrienteFilters.tipoEntidad !== 'todos' ? {
            tipoEntidad: cuentaCorrienteFilters.tipoEntidad,
            idUsuarioEntidad: cuentaCorrienteFilters.idUsuarioEntidad
          } : {})}
          newLabel="Nuevo movimiento"
        />
        <div className="card-content">
          <section className="particular-dashboard-kpis particular-cc-kpis">
            <DashboardKpiCard icon="fas fa-arrow-trend-up" label="Ingresos" displayValue={formatCurrency(cuentaCorrienteResumen.ingresos)} detail="Según filtros" tone="green" />
            <DashboardKpiCard icon="fas fa-arrow-trend-down" label="Salidas" displayValue={formatCurrency(cuentaCorrienteResumen.salidas)} detail="Según filtros" tone="red" />
            <DashboardKpiCard icon="fas fa-scale-balanced" label="Saldo" displayValue={formatCurrency(cuentaCorrienteResumen.saldo)} detail="Según filtros" tone="blue" />
            <DashboardKpiCard icon="fas fa-calendar-alt" label="Saldo del mes" displayValue={formatCurrency(cuentaCorrienteResumen.saldoMes)} detail={`${formatCurrency(cuentaCorrienteResumen.ingresosMes)} / ${formatCurrency(cuentaCorrienteResumen.salidasMes)}`} tone="storage" />
          </section>

          <div className="particular-cc-filters">
            <label>
              Buscar
              <input
                value={cuentaCorrienteFilters.search}
                onChange={(event) => updateCuentaCorrienteFilters({ search: event.target.value })}
                placeholder="Concepto, notas o entidad"
              />
            </label>
            <label>
              Desde
              <input
                type="date"
                value={cuentaCorrienteFilters.fechaDesde}
                onChange={(event) => updateCuentaCorrienteFilters({ fechaDesde: event.target.value })}
              />
            </label>
            <label>
              Hasta
              <input
                type="date"
                value={cuentaCorrienteFilters.fechaHasta}
                onChange={(event) => updateCuentaCorrienteFilters({ fechaHasta: event.target.value })}
              />
            </label>
            <label>
              Tipo
              <SearchableSelect
                value={cuentaCorrienteFilters.tipoMovimiento}
                onChange={(value) => updateCuentaCorrienteFilters({ tipoMovimiento: value || 'todos' })}
                options={movementTypeOptions}
                placeholder="Todos"
              />
            </label>
            <label>
              Entidad
              <SearchableSelect
                value={cuentaCorrienteFilters.tipoEntidad}
                onChange={(value) => updateCuentaCorrienteFilters({ tipoEntidad: value || 'todos', idUsuarioEntidad: '' })}
                options={entityTypeOptions}
                placeholder="Todas"
              />
            </label>
            {(cuentaCorrienteFilters.tipoEntidad === 'Alumno' || cuentaCorrienteFilters.tipoEntidad === 'Profesor') && (
              <label>
                {cuentaCorrienteFilters.tipoEntidad}
                <SearchableSelect
                  value={cuentaCorrienteFilters.idUsuarioEntidad}
                  onChange={(value) => updateCuentaCorrienteFilters({ idUsuarioEntidad: value || '' })}
                  options={entityOptions}
                  placeholder={`Seleccionar ${cuentaCorrienteFilters.tipoEntidad.toLowerCase()}`}
                />
              </label>
            )}
          </div>

          <div className="particular-cc-toolbar">
            <div className="particular-cc-toolbar-summary">
              {cuentaCorrienteTotal === 0
                ? 'Sin movimientos'
                : `${formatDashboardNumber(cuentaCorrienteTotal)} movimientos filtrados`}
            </div>
            <div className="particular-cc-filter-actions">
              <button type="button" className="particular-secondary-btn" onClick={() => setCuentaCorrienteFilters(createCuentaCorrienteFilters())}>
                <i className="fas fa-eraser" />
                Limpiar
              </button>
              <div className="particular-page-size">
                <span>Mostrar</span>
                <SearchableSelect
                  value={String(pageSize)}
                  onChange={(value) => setCuentaCorrienteFilters((prev) => ({ ...prev, pageSize: Number(value) || DEFAULT_GRID_PAGE_SIZE, page: 1 }))}
                  options={PAGE_SIZE_OPTIONS}
                  placeholder={`${DEFAULT_GRID_PAGE_SIZE}`}
                />
              </div>
              <TableExportActions
                columns={cuentaCorrienteExportColumns}
                rows={cuentaCorrienteMovimientos}
                fileName={`cuenta-corriente-${particularName || 'particular'}`}
                title={`Cuenta corriente - ${particularName || 'Particular'}`}
                compact
                disabled={!cuentaCorrienteMovimientos.length}
              />
            </div>
          </div>

          <div className={`particular-table-wrapper${cuentaCorrienteLoading ? ' is-loading' : ''}`}>
            <table className="particular-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Entidad</th>
                  <th>Concepto</th>
                  <th>Importe</th>
                  <th>Notas</th>
                  <th className="actions">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cuentaCorrienteMovimientos.map((movimiento) => {
                  const tipo = getMovimientoTipo(movimiento);
                  const esCredito = tipo === 'Credito';
                  return (
                    <tr key={getId(movimiento)}>
                      <td>{formatDateOnly(getMovimientoFecha(movimiento))}</td>
                      <td className="particular-movement-type-cell">
                        <span className={`particular-movement-pill ${esCredito ? 'income' : 'expense'}`}>
                          {esCredito ? 'Ingreso' : 'Salida'}
                        </span>
                      </td>
                      <td>
                        <strong>{getFullName(getMovimientoEntidad(movimiento), 'Empresa')}</strong>
                        <span className="particular-muted-cell">{getMovimientoEntidadTipo(movimiento)}</span>
                      </td>
                      <td>{getMovimientoConcepto(movimiento)}</td>
                      <td className={`particular-money-cell ${esCredito ? 'particular-money-positive' : 'particular-money-negative'}`}>
                        {formatCurrency(getMovimientoImporteFirmado(movimiento))}
                      </td>
                      <td>{getMovimientoNotas(movimiento) || '-'}</td>
                      <td className="actions">
                        <button type="button" className="particular-icon-btn action-edit" onClick={() => openCuentaCorrienteMovimientoModal(movimiento)} title="Editar movimiento">
                          <i className="fas fa-pen" />
                        </button>
                        <button type="button" className="particular-icon-btn danger" onClick={() => deleteCuentaCorrienteMovimiento(movimiento)} disabled={saving} title="Eliminar movimiento">
                          <i className="fas fa-trash" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {cuentaCorrienteMovimientos.length === 0 && (
                  <TableEmpty colSpan={7} text={cuentaCorrienteLoading ? 'Cargando movimientos...' : 'No hay movimientos para los filtros seleccionados.'} />
                )}
              </tbody>
            </table>
            {cuentaCorrienteLoading && <GridLoadingOverlay />}
          </div>
          <GridPagination
            grid={pageGrid}
            onPageChange={(page) => setCuentaCorrienteFilters((prev) => ({ ...prev, page }))}
          />
        </div>
      </section>
    );
  };

  const renderRestrictedSection = ({ icon, title, message }) => (
    <section className="content-card particular-section">
      <SectionHeader icon={icon} title={title} subtitle="Acceso restringido" />
      <div className="card-content">
        <div className="empty-state">
          <i className="fas fa-lock" />
          <h3>Sin permisos</h3>
          <p>{message}</p>
        </div>
      </div>
    </section>
  );

  const renderCursos = () => (
    <section className="content-card particular-section">
      <SectionHeader
        icon="fas fa-layer-group"
        title="Cursos"
        subtitle={`${cursos.length} cursos cargados`}
        onNew={puedeGestionarCatalogo ? () => openCursoModal() : null}
      />
      <div className="card-content">
        {renderCursosTable(cursos, 'No hay cursos creados.', 'cursos')}
      </div>
    </section>
  );

  const renderAulas = () => {
    const state = gridState.aulas || createGridState();
    const grid = getPagedGrid(aulas, state, getAulaSearchText);

    return (
      <section className="content-card particular-section">
        <SectionHeader
          icon="fas fa-door-open"
          title="Aulas"
          subtitle={`${aulas.length} aulas cargadas`}
          onNew={() => openAulaModal()}
        />
        <div className="card-content">
          <GridToolbar
            state={state}
            grid={grid}
            placeholder="Buscar por aula, curso o nickname"
            onSearchChange={(search) => updateGridSearch('aulas', search)}
            onPageSizeChange={(pageSize) => updateGridPageSize('aulas', pageSize)}
            exportColumns={aulaExportColumns}
            exportRows={grid.filteredItems}
            exportFileName={`aulas-${particularName || 'particular'}`}
            exportTitle={`Aulas - ${particularName || 'Particular'}`}
          />
          <div className={tableWrapperClassName}>
            <table className="particular-table">
              <thead>
                <tr>
                  <th>Aula</th>
                  <th>Curso</th>
                  <th>Nickname</th>
                  <th>Asistentes</th>
                  <th className="actions">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {grid.items.map((aula) => (
                  <tr key={getId(aula)}>
                    <td>
                      <strong>{getName(aula)}</strong>
                      <span>{getDescripcion(aula) || 'Sin descripción'}</span>
                    </td>
                    <td>{aula?.cursoNombre || aula?.CursoNombre || '-'}</td>
                    <td>{aula?.nicknameUrl || aula?.NicknameUrl || '-'}</td>
                    <td>{aula?.cantidadAsistentes ?? aula?.CantidadAsistentes ?? 0}</td>
                    <td className="actions">
                      <button type="button" className="particular-icon-btn action-edit" onClick={() => openAula(aula)} title="Ingresar">
                        <i className="fas fa-external-link-alt" />
                      </button>
                      <button type="button" className="particular-icon-btn resources" onClick={() => openAulaResourcesModal(aula)} title="Recursos">
                        <i className="fas fa-box-open" />
                      </button>
                      <button type="button" className="particular-icon-btn stats" onClick={() => openAulaStatsModal(aula)} title="Estadísticas">
                        <i className="fas fa-chart-line" />
                      </button>
                      <button type="button" className="particular-icon-btn action-edit" onClick={() => openAulaModal(aula)} title="Editar">
                        <i className="fas fa-pen" />
                      </button>
                      <button type="button" className="particular-icon-btn danger" onClick={() => deleteAula(aula)} title="Eliminar">
                        <i className="fas fa-trash" />
                      </button>
                    </td>
                  </tr>
                ))}
                {grid.items.length === 0 && (
                  <TableEmpty colSpan={5} text={state.search ? 'No hay resultados para la búsqueda.' : 'No hay aulas creadas.'} />
                )}
              </tbody>
            </table>
            {isRefreshing && <GridLoadingOverlay />}
          </div>
          <GridPagination grid={grid} onPageChange={(page) => updateGridPage('aulas', page)} />
        </div>
      </section>
    );
  };

  const renderAsistentes = () => {
    const state = gridState.asistentes || createGridState();
    const grid = getPagedGrid(asistentes, state, getAssistantSearchText);

    return (
      <section className="content-card particular-section">
        <SectionHeader
        icon="fas fa-robot"
        title="Asistentes IA"
        subtitle={`${asistentes.length} asistentes disponibles`}
      >
          {isGeneralAdmin && (
            <button
              type="button"
              className="particular-secondary-btn"
              onClick={() => navigate('/admin/solicitudes?tipoTenant=particular', { state: { tipoTenant: 'particular' } })}
            >
              <i className="fas fa-inbox" />
              Solicitudes
            </button>
          )}
          <button type="button" className="particular-secondary-btn" onClick={() => openAsistentesModal()} disabled={!aulas.length}>
            <i className="fas fa-link" />
            Asignar a aula
          </button>
          <button type="button" className="particular-primary-btn" onClick={() => navigate('/mis-agentes-clasicos-gpt/nuevo', { state: { scope: 'particulares' } })}>
            <i className="fas fa-plus" />
            Nuevo asistente IA
          </button>
        </SectionHeader>
        <div className="card-content">
          <GridToolbar
            state={state}
            grid={grid}
            placeholder="Buscar por asistente, descripción o estado"
            onSearchChange={(search) => updateGridSearch('asistentes', search)}
            onPageSizeChange={(pageSize) => updateGridPageSize('asistentes', pageSize)}
            exportColumns={asistenteExportColumns}
            exportRows={grid.filteredItems}
            exportFileName={`asistentes-ia-${particularName || 'particular'}`}
            exportTitle={`Asistentes IA - ${particularName || 'Particular'}`}
          />
          <div className={tableWrapperClassName}>
            <table className="particular-table">
              <thead>
                <tr>
                  <th>Asistente</th>
                  <th>Descripción</th>
                  <th>Estado</th>
                  <th className="actions">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {grid.items.map((asistente) => {
                  const url = getAssistantUrl(asistente);
                  const listo = getBool(asistente, 'gptClasicoCreacionFinalizada', 'GPTClasicoCreacionFinalizada', false);
                  return (
                    <tr key={getAssistantId(asistente)}>
                      <td><strong>{getAssistantName(asistente)}</strong></td>
                      <td>{getAssistantDescription(asistente) || '-'}</td>
                      <td><StatusPill active={listo} trueText="Listo" falseText="Pendiente" /></td>
                      <td className="actions">
                        <button
                          type="button"
                          className="particular-icon-btn action-edit"
                          onClick={() => openAssistantEdit(asistente)}
                          disabled={!getAssistantId(asistente)}
                          title="Editar asistente IA"
                        >
                          <i className="fas fa-pen" />
                        </button>
                        <button
                          type="button"
                          className="particular-icon-btn share"
                          onClick={() => openAssistantShareModal(asistente)}
                          disabled={!url}
                          title="Compartir con QR"
                        >
                          <i className="fas fa-share-alt" />
                        </button>
                        <button
                          type="button"
                          className="particular-icon-btn test"
                          disabled={!url}
                          onClick={() => openAssistantPublicLink(asistente)}
                          title="Probar asistente IA"
                        >
                          <i className="fas fa-play" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {grid.items.length === 0 && (
                  <TableEmpty colSpan={4} text={state.search ? 'No hay resultados para la búsqueda.' : 'No hay asistentes creados.'} />
                )}
              </tbody>
            </table>
            {isRefreshing && <GridLoadingOverlay />}
          </div>
          <GridPagination grid={grid} onPageChange={(page) => updateGridPage('asistentes', page)} />
        </div>
      </section>
    );
  };

  const renderCursoDetailModal = () => {
    const { type, curso } = courseDetailModal;
    if (!curso) return null;

    const cursoNombre = getName(curso);
    const search = normalizeSearchText(courseDetailSearch);
    const isProfesores = type === 'profesores';
    const isAlumnos = type === 'alumnos';
    const rows = isProfesores
      ? getArray(curso?.profesores || curso?.Profesores)
      : isAlumnos
        ? getArray(curso?.alumnos || curso?.Alumnos)
        : getCursoAulas(curso);
    const searchGetter = isProfesores ? getProfesorAsociadoSearchText : isAlumnos ? getAlumnoSearchText : getAulaSearchText;
    const filteredRows = search
      ? rows.filter((item) => normalizeSearchText(searchGetter(item)).includes(search))
      : rows;
    const config = isProfesores
      ? {
        title: 'Profesores del curso',
        icon: 'fas fa-user-tie',
        placeholder: 'Buscar profesor por nombre, email, DNI o teléfono',
        empty: 'Este curso no tiene profesores asociados.',
        emptySearch: 'No hay profesores que coincidan con la búsqueda.',
        exportColumns: profesorExportColumns,
        fileName: `curso-profesores-${cursoNombre}`,
        exportTitle: `Profesores de ${cursoNombre}`
      }
      : isAlumnos
        ? {
          title: 'Alumnos del curso',
          icon: 'fas fa-user-graduate',
          placeholder: 'Buscar alumno por nombre, email, DNI o teléfono',
          empty: 'Este curso no tiene alumnos asociados.',
          emptySearch: 'No hay alumnos que coincidan con la búsqueda.',
          exportColumns: alumnoExportColumns,
          fileName: `curso-alumnos-${cursoNombre}`,
          exportTitle: `Alumnos de ${cursoNombre}`
        }
        : {
          title: 'Aulas del curso',
          icon: 'fas fa-door-open',
          placeholder: 'Buscar aula por nombre, descripción o nickname',
          empty: 'Este curso no tiene aulas creadas.',
          emptySearch: 'No hay aulas que coincidan con la búsqueda.',
          exportColumns: aulaExportColumns,
          fileName: `curso-aulas-${cursoNombre}`,
          exportTitle: `Aulas de ${cursoNombre}`
        };

    const renderRows = () => {
      if (isProfesores) {
        return filteredRows.map((profesor) => {
          const activo = getBool(profesor, 'activo', 'Activo', true);
          const profesorId = getId(profesor);
          const removing = courseDetailRemovingKey === `profesores:${profesorId}`;
          return (
            <tr key={profesorId}>
              <td>
                <strong>{getFullName(profesor, getEmail(profesor))}</strong>
                <span>{profesor?.rol || profesor?.Rol || 'ProfesorParticular'}</span>
              </td>
              <td>{getEmail(profesor) || '-'}</td>
              <td>{profesor?.dni || profesor?.Dni || '-'}</td>
              <td>{profesor?.telefono || profesor?.Telefono || '-'}</td>
              <td><StatusPill active={activo} /></td>
              <td className="actions">
                <button
                  type="button"
                  className="particular-icon-btn danger"
                  onClick={() => removeCursoMember('profesores', profesor)}
                  disabled={!puedeGestionarCatalogo || saving}
                  title="Desasociar del curso"
                >
                  <i className={`fas ${removing ? 'fa-spinner fa-spin' : 'fa-user-minus'}`} />
                </button>
              </td>
            </tr>
          );
        });
      }

      if (isAlumnos) {
        return filteredRows.map((alumno) => {
          const activo = getBool(alumno, 'activo', 'Activo', true);
          const alumnoId = getId(alumno);
          const removing = courseDetailRemovingKey === `alumnos:${alumnoId}`;
          return (
            <tr key={alumnoId}>
              <td>
                <strong>{getFullName(alumno, getEmail(alumno))}</strong>
                <span>{alumno?.rol || alumno?.Rol || 'AlumnoParticular'}</span>
              </td>
              <td>{getEmail(alumno) || '-'}</td>
              <td>{alumno?.dni || alumno?.Dni || '-'}</td>
              <td>{alumno?.telefono || alumno?.Telefono || '-'}</td>
              <td><StatusPill active={activo} /></td>
              <td className="actions">
                <button
                  type="button"
                  className="particular-icon-btn danger"
                  onClick={() => removeCursoMember('alumnos', alumno)}
                  disabled={!puedeGestionarCatalogo || saving}
                  title="Desasociar del curso"
                >
                  <i className={`fas ${removing ? 'fa-spinner fa-spin' : 'fa-user-minus'}`} />
                </button>
              </td>
            </tr>
          );
        });
      }

      return filteredRows.map((aula) => (
        <tr key={getId(aula)}>
          <td>
            <strong>{getName(aula)}</strong>
            <span>{getDescripcion(aula) || 'Sin descripción'}</span>
          </td>
          <td>{aula?.nicknameUrl || aula?.NicknameUrl || '-'}</td>
          <td>{aula?.cantidadAsistentes ?? aula?.CantidadAsistentes ?? 0}</td>
          <td className="actions">
            <button type="button" className="particular-icon-btn action-edit" onClick={() => openAula(aula)} title="Ingresar">
              <i className="fas fa-external-link-alt" />
            </button>
            <button type="button" className="particular-icon-btn resources" onClick={() => openAulaResourcesModal(aula)} title="Recursos">
              <i className="fas fa-box-open" />
            </button>
            <button type="button" className="particular-icon-btn stats" onClick={() => openAulaStatsModal(aula)} title="Estadísticas">
              <i className="fas fa-chart-line" />
            </button>
            <button type="button" className="particular-icon-btn action-edit" onClick={() => openAulaModal(aula)} title="Editar">
              <i className="fas fa-pen" />
            </button>
          </td>
        </tr>
      ));
    };

    return (
      <Modal title={config.title} icon={config.icon} onClose={closeModal} className="particular-modal-wide">
        <div className="particular-modal-body">
          <div className="particular-detail-summary">
            <span>Curso seleccionado</span>
            <strong>{cursoNombre}</strong>
            {getDescripcion(curso) && <p>{getDescripcion(curso)}</p>}
          </div>

          <div className="particular-detail-toolbar">
            <label className="particular-grid-search">
              <span>Buscar</span>
              <div className="particular-grid-search-box">
                <i className="fas fa-search" />
                <input
                  value={courseDetailSearch}
                  onChange={(event) => setCourseDetailSearch(event.target.value)}
                  placeholder={config.placeholder}
                />
              </div>
            </label>
            <TableExportActions
              columns={config.exportColumns}
              rows={filteredRows}
              fileName={config.fileName}
              title={config.exportTitle}
              compact
              disabled={!filteredRows.length}
            />
          </div>

          {rows.length === 0 ? (
            <div className="particular-empty-inline">{config.empty}</div>
          ) : filteredRows.length === 0 ? (
            <div className="particular-empty-inline">{config.emptySearch}</div>
          ) : (
            <div className="particular-table-wrapper particular-detail-table-wrapper">
              <table className="particular-table">
                <thead>
                  {isProfesores || isAlumnos ? (
                    <tr>
                      <th>{isProfesores ? 'Profesor' : 'Alumno'}</th>
                      <th>Email</th>
                      <th>DNI</th>
                      <th>Teléfono</th>
                      <th>Estado</th>
                      <th className="actions">Acciones</th>
                    </tr>
                  ) : (
                    <tr>
                      <th>Aula</th>
                      <th>Nickname</th>
                      <th>Asistentes</th>
                      <th className="actions">Acciones</th>
                    </tr>
                  )}
                </thead>
                <tbody>{renderRows()}</tbody>
              </table>
            </div>
          )}

          <div className="particular-modal-footer">
            <button type="button" className="particular-secondary-btn" onClick={closeModal} disabled={saving}>Cerrar</button>
          </div>
        </div>
      </Modal>
    );
  };

  const renderModal = () => {
    if (!modalType) return null;

    if (modalType === 'cursoDetalle') {
      return renderCursoDetailModal();
    }

    if (modalType === 'resetPassword') {
      const resetInfo = getResetPasswordInfo(resetPasswordModal.type, resetPasswordModal.target);
      return (
        <Modal title={resetInfo.title} icon="fas fa-key" onClose={closeModal}>
          <div className="particular-modal-body">
            <div className="particular-reset-target">
              <i className={resetInfo.icon} />
              <div>
                <span>Usuario seleccionado</span>
                <strong>{resetInfo.name}</strong>
                {resetInfo.email && <p>{resetInfo.email}</p>}
              </div>
            </div>

            <div className="particular-reset-warning">
              <i className="fas fa-exclamation-triangle" />
              <div>
                <strong>Esta acción reemplaza la contraseña actual.</strong>
                <p>
                  El {resetInfo.userLabel} dejará de acceder con su password anterior. No se modifican sus datos,
                  permisos ni estado de acceso.
                </p>
              </div>
            </div>

            <div className="particular-reset-password">
              <span>Nuevo password</span>
              <code>{resetInfo.newPassword || 'Sin DNI registrado'}</code>
              <small>Origen: {resetInfo.passwordSource}.</small>
            </div>

            {!resetInfo.canConfirm && (
              <div className="particular-info-callout error">
                <i className="fas fa-info-circle" />
                <span>Para resetear la contraseña de este profesor primero cargá un DNI en su ficha.</span>
              </div>
            )}

            <div className="particular-modal-footer">
              <button type="button" className="particular-secondary-btn" onClick={closeModal} disabled={saving}>Cancelar</button>
              <button
                type="button"
                className="particular-primary-btn"
                onClick={confirmResetPassword}
                disabled={saving || !resetInfo.canConfirm}
              >
                <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-key'}`} />
                {saving ? 'Reseteando...' : 'Confirmar reset'}
              </button>
            </div>
          </div>
        </Modal>
      );
    }

    if (modalType === 'etiqueta') {
      return (
        <Modal title={etiquetaForm.id ? 'Editar etiqueta' : 'Nueva etiqueta'} icon="fas fa-tags" onClose={closeModal}>
          <form className="particular-modal-body" onSubmit={saveEtiqueta}>
            <label>Nombre<input value={etiquetaForm.nombre} onChange={(event) => setEtiquetaForm((prev) => ({ ...prev, nombre: event.target.value }))} required /></label>
            <label>Descripción<textarea value={etiquetaForm.descripcion} onChange={(event) => setEtiquetaForm((prev) => ({ ...prev, descripcion: event.target.value }))} /></label>
            <label className="particular-color-field">
              Color
              <div>
                <input type="color" value={etiquetaForm.color} onChange={(event) => setEtiquetaForm((prev) => ({ ...prev, color: event.target.value }))} />
                <span>{etiquetaForm.color}</span>
              </div>
            </label>
            <div className="particular-modal-footer">
              <button type="button" className="particular-secondary-btn" onClick={closeModal} disabled={saving}>Cancelar</button>
              <SubmitButton loading={saving} disabled={!puedeGestionarCatalogo} />
            </div>
          </form>
        </Modal>
      );
    }

    if (modalType === 'alumno') {
      return (
        <Modal title={alumnoForm.id ? 'Editar alumno' : 'Nuevo alumno'} icon="fas fa-user-graduate" onClose={closeModal}>
          <form className="particular-modal-body" onSubmit={saveAlumno}>
            <div className="particular-modal-grid">
              <label>Email<input type="email" value={alumnoForm.email} onChange={(event) => setAlumnoForm((prev) => ({ ...prev, email: event.target.value }))} required /></label>
              <label>DNI<input value={alumnoForm.dni} onChange={(event) => setAlumnoForm((prev) => ({ ...prev, dni: event.target.value }))} required /></label>
              <label>Nombre<input value={alumnoForm.nombre} onChange={(event) => setAlumnoForm((prev) => ({ ...prev, nombre: event.target.value }))} /></label>
              <label>Apellido<input value={alumnoForm.apellido} onChange={(event) => setAlumnoForm((prev) => ({ ...prev, apellido: event.target.value }))} /></label>
              <label>Teléfono<input value={alumnoForm.telefono} onChange={(event) => setAlumnoForm((prev) => ({ ...prev, telefono: event.target.value }))} /></label>
              {alumnoForm.id && (
                <ToggleField checked={alumnoForm.activo} label="Activo" onChange={(value) => setAlumnoForm((prev) => ({ ...prev, activo: value }))} />
              )}
            </div>
            <div className="particular-modal-footer">
              <button type="button" className="particular-secondary-btn" onClick={closeModal} disabled={saving}>Cancelar</button>
              <SubmitButton loading={saving} disabled={!puedeAdministrar} />
            </div>
          </form>
        </Modal>
      );
    }

    if (modalType === 'invitacion') {
      return (
        <Modal title={invitacionForm.id ? 'Editar invitación' : 'Nueva invitación'} icon="fas fa-ticket-alt" onClose={closeModal}>
          <form className="particular-modal-body" onSubmit={saveInvitacion}>
            <div className="particular-modal-grid">
              <label>
                Código de invitación
                <input
                  value={invitacionForm.codigo}
                  onChange={(event) => setInvitacionForm((prev) => ({ ...prev, codigo: event.target.value }))}
                  minLength={6}
                  maxLength={80}
                  required
                />
              </label>
              <label>
                Fecha de caducidad
                <input
                  type="date"
                  value={invitacionForm.fechaCaducidad}
                  onChange={(event) => setInvitacionForm((prev) => ({ ...prev, fechaCaducidad: event.target.value }))}
                  required
                />
              </label>
              {invitacionForm.id && (
                <ToggleField checked={invitacionForm.activo} label="Activa" onChange={(value) => setInvitacionForm((prev) => ({ ...prev, activo: value }))} />
              )}
            </div>
            <div className="particular-info-callout">
              <i className="fas fa-info-circle" />
              <span>El alumno invitado completará sus datos en un link público. Para el primer acceso su contraseña será el DNI informado.</span>
            </div>
            <div className="particular-modal-footer">
              <button type="button" className="particular-secondary-btn" onClick={closeModal} disabled={saving}>Cancelar</button>
              <SubmitButton loading={saving} disabled={!puedeGestionarInvitaciones} />
            </div>
          </form>
        </Modal>
      );
    }

    if (modalType === 'invitacionShare') {
      const code = getInvitationCode(invitacionShare);
      const link = invitacionShare?.link || buildInvitationLink(code);
      const qrUrl = link ? `https://quickchart.io/qr?text=${encodeURIComponent(link)}&size=360&margin=2&ecLevel=M` : '';
      return (
        <Modal title="Compartir invitación" icon="fas fa-share-alt" onClose={closeModal} className="particular-modal-wide">
          <div className="particular-modal-body">
            <div className="particular-invitation-share">
              <div className="particular-invitation-card">
                <div className="particular-invitation-brand">
                  <img src={brandLogo} alt="aluxIA" />
                  <div>
                    <span>aluxIA</span>
                    <strong>Invitación para alumnos</strong>
                  </div>
                </div>
                <div className="particular-invitation-copy">
                  <span>Cuenta particular</span>
                  <h4>{particularName || 'Particular'}</h4>
                  <p>Completá el formulario de alta y usá tu DNI como contraseña en el primer acceso.</p>
                </div>
                <div className="particular-invitation-qr">
                  {qrUrl ? <img src={qrUrl} alt="QR de invitación" /> : <i className="fas fa-qrcode" />}
                </div>
                <div className="particular-invitation-code">
                  <span>Código</span>
                  <strong>{code || '-'}</strong>
                </div>
                <div className="particular-invitation-link">
                  <span>Link directo</span>
                  <p>{link}</p>
                </div>
              </div>
            </div>
            <div className="particular-modal-footer">
              <button type="button" className="particular-secondary-btn" onClick={closeModal}>Cerrar</button>
              <button type="button" className="particular-secondary-btn" onClick={() => copyText(code, 'Código copiado.')}>
                <i className="fas fa-copy" />
                Copiar código
              </button>
              <button type="button" className="particular-secondary-btn" onClick={() => copyText(link, 'Link copiado.')}>
                <i className="fas fa-link" />
                Copiar link
              </button>
              <button
                type="button"
                className="particular-secondary-btn"
                onClick={handleDownloadInvitationImage}
                disabled={invitacionShareDownloading || !code || !link}
              >
                <i className={invitacionShareDownloading ? 'fas fa-spinner fa-spin' : 'fas fa-download'} />
                {invitacionShareDownloading ? 'Generando...' : 'Descargar imagen'}
              </button>
              <a className="particular-primary-btn" href={link} target="_blank" rel="noopener noreferrer">
                <i className="fas fa-external-link-alt" />
                Abrir
              </a>
            </div>
          </div>
        </Modal>
      );
    }

    if (modalType === 'asistenteShare') {
      const asistente = asistenteShare?.asistente || {};
      const link = asistenteShare?.link || buildAssistantPublicLink(asistente);
      const qrUrl = link ? `https://quickchart.io/qr?text=${encodeURIComponent(link)}&size=360&margin=2&ecLevel=M` : '';
      const nombre = getAssistantName(asistente);
      const descripcion = getAssistantDescription(asistente);

      return (
        <Modal title="Compartir asistente IA" icon="fas fa-share-alt" onClose={closeModal} className="particular-modal-wide">
          <div className="particular-modal-body">
            <div className="particular-invitation-share">
              <div className="particular-invitation-card">
                <div className="particular-invitation-brand">
                  <img src={brandLogo} alt="aluxIA" />
                  <div>
                    <span>aluxIA</span>
                    <strong>Asistente IA compartible</strong>
                  </div>
                </div>
                <div className="particular-invitation-copy">
                  <span>Cuenta particular</span>
                  <h4>{particularName || 'Particular'}</h4>
                  <p>{nombre}</p>
                  {descripcion && <p>{descripcion}</p>}
                </div>
                <div className="particular-invitation-qr">
                  {qrUrl ? <img src={qrUrl} alt={`QR para ${nombre}`} /> : <i className="fas fa-qrcode" />}
                </div>
                <div className="particular-invitation-link">
                  <span>Link directo</span>
                  <p>{link}</p>
                </div>
              </div>
            </div>
            <div className="particular-modal-footer">
              <button type="button" className="particular-secondary-btn" onClick={closeModal}>Cerrar</button>
              <button type="button" className="particular-secondary-btn" onClick={() => copyText(link, 'Link copiado.')} disabled={!link}>
                <i className="fas fa-copy" />
                Copiar link
              </button>
              <a className="particular-primary-btn" href={link} target="_blank" rel="noopener noreferrer">
                <i className="fas fa-external-link-alt" />
                Probar
              </a>
            </div>
          </div>
        </Modal>
      );
    }

    if (modalType === 'invitacionDetalle') {
      const registros = getInvitationRegistrations(invitacionDetail);
      return (
        <Modal title="Detalle de invitación" icon="fas fa-users" onClose={closeModal} className="particular-modal-wide">
          <div className="particular-modal-body">
            <div className="particular-detail-summary">
              <span>Código</span>
              <strong>{getInvitationCode(invitacionDetail) || '-'}</strong>
              <p>Estado: {getInvitationStatus(invitacionDetail)} · Caducidad: {formatDateOnly(getInvitationExpiration(invitacionDetail))}</p>
            </div>
            {invitacionDetailLoading ? (
              <LoadingSpinner message="Cargando registrados..." size="small" inline />
            ) : registros.length === 0 ? (
              <div className="particular-empty-inline">Todavía no hay alumnos registrados con esta invitación.</div>
            ) : (
              <div className="particular-table-wrapper particular-detail-table-wrapper">
                <table className="particular-table">
                  <thead>
                    <tr>
                      <th>Alumno</th>
                      <th>Email</th>
                      <th>DNI</th>
                      <th>Fecha registro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registros.map((registro) => {
                      const alumno = registro?.alumno || registro?.Alumno || {};
                      return (
                        <tr key={getId(registro) || getId(alumno)}>
                          <td><strong>{getFullName(alumno, getEmail(alumno))}</strong></td>
                          <td>{getEmail(alumno) || '-'}</td>
                          <td>{alumno?.dni || alumno?.Dni || '-'}</td>
                          <td>{formatDashboardDateTime(registro?.fechaRegistroUtc || registro?.FechaRegistroUtc)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div className="particular-modal-footer">
              <button type="button" className="particular-secondary-btn" onClick={closeModal}>Cerrar</button>
              <button type="button" className="particular-primary-btn" onClick={() => openInvitacionShareModal(invitacionDetail)} disabled={!invitacionDetail}>
                <i className="fas fa-share-alt" />
                Compartir nuevamente
              </button>
            </div>
          </div>
        </Modal>
      );
    }

    if (modalType === 'cuentaCorrienteMovimiento') {
      const movementTypeOptions = [
        { value: 'Debito', label: 'Débito' },
        { value: 'Credito', label: 'Crédito' }
      ];
      const entityTypeOptions = [
        { value: 'Alumno', label: 'Alumno' },
        { value: 'Profesor', label: 'Profesor' },
        { value: 'Empresa', label: 'Empresa' }
      ];
      const entityOptions = cuentaCorrienteForm.tipoEntidad === 'Profesor'
        ? profesorSelectOptions
        : cuentaCorrienteForm.tipoEntidad === 'Alumno'
          ? alumnoSelectOptions
          : [];
      const needsEntity = cuentaCorrienteForm.tipoEntidad === 'Alumno' || cuentaCorrienteForm.tipoEntidad === 'Profesor';
      const importeInvalido = parseCuentaCorrienteAmountInput(cuentaCorrienteForm.importe) <= 0;

      return (
        <Modal title={cuentaCorrienteForm.id ? 'Editar movimiento' : 'Nuevo movimiento'} icon="fas fa-file-invoice-dollar" onClose={closeModal}>
          <form className="particular-modal-body" onSubmit={saveCuentaCorrienteMovimiento}>
            <div className="particular-modal-grid">
              <label>
                Fecha
                <input
                  type="date"
                  value={cuentaCorrienteForm.fechaMovimiento}
                  onChange={(event) => setCuentaCorrienteForm((prev) => ({ ...prev, fechaMovimiento: event.target.value }))}
                  required
                />
              </label>
              <div className="particular-form-group">
                <span>Tipo</span>
                <SearchableSelect
                  value={cuentaCorrienteForm.tipoMovimiento}
                  onChange={(value) => setCuentaCorrienteForm((prev) => ({ ...prev, tipoMovimiento: value || 'Debito' }))}
                  options={movementTypeOptions}
                  placeholder="Seleccionar tipo"
                />
              </div>
              <div className="particular-form-group">
                <span>Entidad</span>
                <SearchableSelect
                  value={cuentaCorrienteForm.tipoEntidad}
                  onChange={(value) => setCuentaCorrienteForm((prev) => ({
                    ...prev,
                    tipoEntidad: value || 'Alumno',
                    idUsuarioEntidad: ''
                  }))}
                  options={entityTypeOptions}
                  placeholder="Seleccionar entidad"
                />
              </div>
              {needsEntity ? (
                <div className="particular-form-group">
                  <span>{cuentaCorrienteForm.tipoEntidad}</span>
                  <SearchableSelect
                    value={cuentaCorrienteForm.idUsuarioEntidad}
                    onChange={(value) => setCuentaCorrienteForm((prev) => ({ ...prev, idUsuarioEntidad: value || '' }))}
                    options={entityOptions}
                    placeholder={`Seleccionar ${cuentaCorrienteForm.tipoEntidad.toLowerCase()}`}
                    emptyText={`No hay ${cuentaCorrienteForm.tipoEntidad.toLowerCase()}s cargados.`}
                  />
                </div>
              ) : (
                <div className="particular-form-group">
                  <span>Entidad</span>
                  <div className="particular-cc-company-target">
                    <i className="fas fa-building" />
                    <strong>Empresa</strong>
                  </div>
                </div>
              )}
              <label>
                Concepto
                <input
                  value={cuentaCorrienteForm.concepto}
                  onChange={(event) => setCuentaCorrienteForm((prev) => ({ ...prev, concepto: event.target.value }))}
                  maxLength={200}
                  required
                />
              </label>
              <label>
                Importe
                <input
                  type="text"
                  inputMode="decimal"
                  value={cuentaCorrienteForm.importe}
                  onChange={(event) => setCuentaCorrienteForm((prev) => ({
                    ...prev,
                    importe: formatCuentaCorrienteAmountInput(event.target.value)
                  }))}
                  onBlur={() => setCuentaCorrienteForm((prev) => ({
                    ...prev,
                    importe: formatCuentaCorrienteAmountFromNumber(parseCuentaCorrienteAmountInput(prev.importe))
                  }))}
                  placeholder="0,00"
                  required
                />
              </label>
            </div>
            <label>
              Notas
              <textarea
                value={cuentaCorrienteForm.notas}
                onChange={(event) => setCuentaCorrienteForm((prev) => ({ ...prev, notas: event.target.value }))}
                maxLength={1000}
              />
            </label>
            <div className="particular-modal-footer">
              <button type="button" className="particular-secondary-btn" onClick={closeModal} disabled={saving}>Cancelar</button>
              <SubmitButton
                loading={saving}
                disabled={!puedeGestionarCuentaCorriente || !cuentaCorrienteForm.concepto.trim() || importeInvalido || (needsEntity && !cuentaCorrienteForm.idUsuarioEntidad)}
              />
            </div>
          </form>
        </Modal>
      );
    }

    if (modalType === 'profesorAsociado') {
      return (
        <Modal title={profesorAsociadoForm.id ? 'Editar profesor' : 'Nuevo profesor'} icon="fas fa-user-tie" onClose={closeModal}>
          <form className="particular-modal-body" onSubmit={saveProfesorAsociado}>
            <div className="particular-modal-grid">
              <label>Email<input type="email" value={profesorAsociadoForm.email} onChange={(event) => setProfesorAsociadoForm((prev) => ({ ...prev, email: event.target.value }))} required /></label>
              <label>DNI<input value={profesorAsociadoForm.dni} onChange={(event) => setProfesorAsociadoForm((prev) => ({ ...prev, dni: event.target.value }))} required /></label>
              <label>Nombre<input value={profesorAsociadoForm.nombre} onChange={(event) => setProfesorAsociadoForm((prev) => ({ ...prev, nombre: event.target.value }))} /></label>
              <label>Apellido<input value={profesorAsociadoForm.apellido} onChange={(event) => setProfesorAsociadoForm((prev) => ({ ...prev, apellido: event.target.value }))} /></label>
              <label>Teléfono<input value={profesorAsociadoForm.telefono} onChange={(event) => setProfesorAsociadoForm((prev) => ({ ...prev, telefono: event.target.value }))} /></label>
              {profesorAsociadoForm.id && (
                <ToggleField checked={profesorAsociadoForm.activo} label="Activo" onChange={(value) => setProfesorAsociadoForm((prev) => ({ ...prev, activo: value }))} />
              )}
            </div>
            <div className="particular-modal-footer">
              <button type="button" className="particular-secondary-btn" onClick={closeModal} disabled={saving}>Cancelar</button>
              <SubmitButton loading={saving} disabled={!puedeGestionarProfesores} />
            </div>
          </form>
        </Modal>
      );
    }

    if (modalType === 'curso') {
      return (
        <Modal title={cursoForm.id ? 'Editar curso' : 'Nuevo curso'} icon="fas fa-layer-group" onClose={closeModal}>
          <form className="particular-modal-body" onSubmit={saveCurso}>
            <label>Nombre<input value={cursoForm.nombre} onChange={(event) => setCursoForm((prev) => ({ ...prev, nombre: event.target.value }))} required /></label>
            <label>Descripción<textarea value={cursoForm.descripcion} onChange={(event) => setCursoForm((prev) => ({ ...prev, descripcion: event.target.value }))} /></label>
            <div className="particular-form-group">
              <span>Etiquetas</span>
              <SearchableSelect
                multiple
                value={cursoForm.etiquetaIds}
                values={cursoForm.etiquetaIds}
                onChange={(ids) => setCursoForm((prev) => ({ ...prev, etiquetaIds: ids }))}
                options={etiquetaSelectOptions}
                placeholder="Buscar etiquetas"
                emptyText="No hay etiquetas."
              />
            </div>
            <div className="particular-form-group">
              <span>Profesores</span>
              <SearchableSelect
                multiple
                value={cursoForm.profesorIds}
                values={cursoForm.profesorIds}
                onChange={(ids) => setCursoForm((prev) => ({ ...prev, profesorIds: ids }))}
                options={profesorSelectOptions}
                placeholder="Buscar profesores"
                emptyText="No hay profesores."
              />
            </div>
            <div className="particular-form-group">
              <span>Alumnos</span>
              <SearchableSelect
                multiple
                value={cursoForm.alumnoIds}
                values={cursoForm.alumnoIds}
                onChange={(ids) => setCursoForm((prev) => ({ ...prev, alumnoIds: ids }))}
                options={alumnoSelectOptions}
                placeholder="Buscar alumnos"
                emptyText="No hay alumnos."
              />
            </div>
            <div className="particular-modal-footer">
              <button type="button" className="particular-secondary-btn" onClick={closeModal} disabled={saving}>Cancelar</button>
              <SubmitButton loading={saving} disabled={!puedeGestionarCatalogo} />
            </div>
          </form>
        </Modal>
      );
    }

    if (modalType === 'deleteCurso') {
      const cursoEtiquetasDelete = getArray(cursoToDelete?.etiquetas || cursoToDelete?.Etiquetas);
      const cursoProfesoresDelete = getArray(cursoToDelete?.profesores || cursoToDelete?.Profesores);
      const cursoAlumnosDelete = getArray(cursoToDelete?.alumnos || cursoToDelete?.Alumnos);
      const cantidadAulasDelete = getCursoAulas(cursoToDelete).length || Number(cursoToDelete?.cantidadAulas ?? cursoToDelete?.CantidadAulas ?? 0);

      return (
        <Modal title="Eliminar curso y asociaciones" icon="fas fa-exclamation-triangle" onClose={closeModal} className="particular-modal-wide">
          <div className="particular-modal-body">
            <p className="particular-curso-delete-intro">
              Vas a eliminar el curso <strong>{getName(cursoToDelete, '')}</strong>. Esta acción también desvinculará todo lo que dependa de este curso.
            </p>

            <div className="particular-curso-delete-summary-grid">
              <div className="particular-curso-delete-summary-card">
                <span>Etiquetas asociadas</span>
                <strong>{cursoEtiquetasDelete.length}</strong>
              </div>
              <div className="particular-curso-delete-summary-card">
                <span>Profesores asociados</span>
                <strong>{cursoProfesoresDelete.length}</strong>
              </div>
              <div className="particular-curso-delete-summary-card">
                <span>Alumnos vinculados</span>
                <strong>{cursoAlumnosDelete.length}</strong>
              </div>
              <div className="particular-curso-delete-summary-card">
                <span>Aulas vinculadas</span>
                <strong>{cantidadAulasDelete}</strong>
              </div>
            </div>

            <div className="particular-curso-delete-impact">
              <p>Qué va a pasar</p>
              <ul>
                <li>El curso <strong>{getName(cursoToDelete, '')}</strong> dejará de estar disponible.</li>
                <li>Se quitarán las {cursoEtiquetasDelete.length} etiqueta(s) asociadas a este curso.</li>
                <li>Los {cursoProfesoresDelete.length} profesor(es) asociados perderán el acceso a este curso.</li>
                <li>Los {cursoAlumnosDelete.length} alumno(s) vinculados dejarán de ver este curso.</li>
                <li>Las {cantidadAulasDelete} aula(s) vinculadas se desactivarán junto con este curso.</li>
              </ul>
            </div>

            <div className="particular-curso-delete-confirm-box">
              <label htmlFor="particular-curso-delete-confirm-input">Para confirmar, escribí exactamente:</label>
              <code>{deleteCursoExpectedText}</code>
              <input
                id="particular-curso-delete-confirm-input"
                type="text"
                value={deleteCursoConfirmText}
                onChange={(event) => {
                  setDeleteCursoConfirmText(event.target.value);
                  setDeleteCursoError('');
                }}
                placeholder={deleteCursoExpectedText}
                autoComplete="off"
                disabled={saving}
              />
              {deleteCursoConfirmText.trim() && (
                <span className={`particular-curso-delete-confirm-hint ${canConfirmDeleteCurso ? 'valid' : 'invalid'}`}>
                  {canConfirmDeleteCurso
                    ? 'Confirmación válida. Ya podés eliminar el curso.'
                    : 'La confirmación no coincide con el texto solicitado.'}
                </span>
              )}
            </div>

            {deleteCursoError && (
              <div className="particular-curso-delete-error">
                <i className="fas fa-exclamation-circle" />
                <span>{deleteCursoError}</span>
              </div>
            )}

            <div className="particular-modal-footer">
              <button type="button" className="particular-secondary-btn" onClick={closeModal} disabled={saving}>Cancelar</button>
              <button
                type="button"
                className="particular-danger-btn"
                onClick={handleConfirmDeleteCurso}
                disabled={!canConfirmDeleteCurso || saving}
              >
                {saving ? 'Eliminando...' : 'Eliminar curso'}
              </button>
            </div>
          </div>
        </Modal>
      );
    }

    if (modalType === 'aula') {
      return (
        <Modal title={aulaForm.id ? 'Editar aula' : 'Nueva aula'} icon="fas fa-door-open" onClose={closeModal}>
          <form className="particular-modal-body" onSubmit={saveAula}>
            <div className="particular-form-group">
              <span>Curso</span>
              <SearchableSelect
                value={aulaForm.idCursoParticular}
                onChange={(value) => setAulaForm((prev) => ({ ...prev, idCursoParticular: value }))}
                options={cursoSelectOptions}
                placeholder="Seleccionar curso"
                emptyText="No hay cursos."
              />
            </div>
            <label>Nombre<input value={aulaForm.nombre} onChange={(event) => setAulaForm((prev) => ({ ...prev, nombre: event.target.value }))} required /></label>
            <label>Descripción<textarea value={aulaForm.descripcion} onChange={(event) => setAulaForm((prev) => ({ ...prev, descripcion: event.target.value }))} /></label>
            <div className="particular-form-group">
              <span>Configuración</span>
              <div className="particular-toggle-grid">
                <ToggleField checked={aulaForm.mostrarArchivos} label="Archivos" onChange={(value) => setAulaForm((prev) => ({ ...prev, mostrarArchivos: value }))} />
                <ToggleField checked={aulaForm.mostrarLinks} label="Links" onChange={(value) => setAulaForm((prev) => ({ ...prev, mostrarLinks: value }))} />
                <ToggleField checked={aulaForm.mostrarPizarra} label="Pizarra" onChange={(value) => setAulaForm((prev) => ({ ...prev, mostrarPizarra: value }))} />
                <ToggleField checked={aulaForm.mostrarChat} label="Chat" onChange={(value) => setAulaForm((prev) => ({ ...prev, mostrarChat: value }))} />
                <ToggleField checked={aulaForm.mostrarVideollamada} label="Video" onChange={(value) => setAulaForm((prev) => ({ ...prev, mostrarVideollamada: value }))} />
                <ToggleField checked={aulaForm.mostrarForo} label="Foro" onChange={(value) => setAulaForm((prev) => ({ ...prev, mostrarForo: value }))} />
                <ToggleField checked={aulaForm.mostrarAsistentesIA} label="Asistentes" onChange={(value) => setAulaForm((prev) => ({ ...prev, mostrarAsistentesIA: value }))} />
              </div>
            </div>
            <div className="particular-modal-footer">
              <button type="button" className="particular-secondary-btn" onClick={closeModal} disabled={saving}>Cancelar</button>
              <SubmitButton loading={saving} disabled={!puedeAdministrar} />
            </div>
          </form>
        </Modal>
      );
    }

    return (
      <Modal title="Asignar asistentes al aula" icon="fas fa-robot" onClose={closeModal}>
        <form className="particular-modal-body" onSubmit={saveAulaAssistants}>
          <div className="particular-form-group">
            <span>Aula</span>
            <SearchableSelect
              value={selectedAulaAssistantId}
              onChange={setSelectedAulaAssistantId}
              options={aulaSelectOptions}
              placeholder="Seleccionar aula"
              emptyText="No hay aulas."
            />
          </div>
          {assistantLoading ? (
            <LoadingSpinner message="Cargando asistentes..." size="small" inline />
          ) : (
            <div className="particular-form-group">
              <span>Asistentes</span>
              <SearchableSelect
                multiple
                value={assignedAssistantIds}
                values={assignedAssistantIds}
                onChange={setAssignedAssistantIds}
                options={asistenteSelectOptions}
                placeholder="Buscar asistentes"
                emptyText="No hay asistentes listos para usar."
              />
            </div>
          )}
          <div className="particular-modal-footer">
            <button type="button" className="particular-secondary-btn" onClick={closeModal} disabled={saving}>Cancelar</button>
            <SubmitButton loading={saving} disabled={!selectedAulaAssistantId || !puedeAdministrar} loadingText="Asignando..." />
          </div>
        </form>
      </Modal>
    );
  };

  const renderContent = () => {
    if (isInitialLoading) {
      return <LoadingSpinner message="Cargando dashboard..." size="medium" inline />;
    }
    if (isParticularChatGPTMenuItem({ id: activeMenu })) {
      if (!chatGPTPermissionReady) {
        return <LoadingSpinner message="Verificando suscripción..." size="medium" inline />;
      }
      if (!canUseChatGPTFeatures) {
        return renderResumen();
      }
    }
    if (activeMenu === 'calendario') {
      if (!calendarioPermissionReady) {
        return <LoadingSpinner message="Verificando suscripción..." size="medium" inline />;
      }
      if (!canUseCalendarioFeature) {
        return renderResumen();
      }
      return (
        <ParticularDisponibilidadCalendar
          contexto={contexto}
          profesores={profesores}
          particularName={particularName}
        />
      );
    }
    if (activeMenu === 'etiquetas') return renderEtiquetas();
    if (activeMenu === 'cursos') return renderCursos();
    if (activeMenu === 'profesoresAsociados') {
      return puedeGestionarProfesores
        ? renderProfesoresAsociados()
        : renderRestrictedSection({
          icon: 'fas fa-user-tie',
          title: 'Profesores',
          message: 'No tenés permisos para administrar profesores asociados en esta cuenta.'
        });
    }
    if (activeMenu === 'alumnos') return renderAlumnos();
    if (activeMenu === 'invitaciones') {
      return puedeGestionarInvitaciones
        ? renderInvitaciones()
        : renderRestrictedSection({
          icon: 'fas fa-ticket-alt',
          title: 'Invitaciones',
          message: 'No tenés permisos para administrar invitaciones de alumnos en esta cuenta.'
        });
    }
    if (activeMenu === 'cuentaCorriente') {
      return puedeGestionarCuentaCorriente
        ? renderCuentaCorriente()
        : renderRestrictedSection({
          icon: 'fas fa-file-invoice-dollar',
          title: 'Cta. Cte.',
          message: 'No tenés permisos para administrar la cuenta corriente en esta cuenta.'
        });
    }
    if (activeMenu === 'aulas') return renderAulas();
    if (activeMenu === 'asistentes') return renderAsistentes();
    return renderResumen();
  };

  if (isAlumnoParticular) {
    return <Navigate to="/particular/alumno-panel" replace />;
  }

  return (
    <div className={`new-dashboard particular-dashboard pd-pro ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap" />
            <span>aluxIA</span>
          </div>
        </div>

        <ParticularDashboardMenu
          activeId={activeMenu}
          onNavigate={handleMenuNavigate}
        />

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-tie" />
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">Particular</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header particular-header">
          <div className="header-left">
            <button type="button" className="sidebar-toggle" onClick={() => setIsSidebarOpen((prev) => !prev)} title="Mostrar menú">
              <i className="fas fa-bars" />
            </button>
            <div>
              <h1 className="page-title">
                <i className="fas fa-user-tie" />
                {dashboardHeaderName}
              </h1>
              <p className="page-subtitle">{dashboardSubtitle}</p>
            </div>
          </div>
          <div className="header-right">
            {isGeneralAdmin && (
              <button
                type="button"
                className="inbox-btn-header"
                onClick={() => navigate('/admin/solicitudes?tipoTenant=particular', { state: { tipoTenant: 'particular' } })}
                title="Solicitudes recibidas"
              >
                <i className="fas fa-inbox" />
                Solicitudes recibidas
                {solicitudesPendientes > 0 && (
                  <span className="inbox-badge">{solicitudesPendientes}</span>
                )}
              </button>
            )}
            <button type="button" className="particular-secondary-btn" onClick={loadData} disabled={isLoading}>
              <i className={`fas ${isLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
              {isRefreshing ? 'Actualizando...' : 'Actualizar'}
            </button>
            <UserSessionMenu
              user={user}
              onLogout={logout}
              userFallbackLabel="Particular"
              extraItems={isGeneralAdmin ? [{
                key: 'switch-particular',
                label: 'Cambiar particular',
                icon: 'fas fa-exchange-alt',
                onClick: handleChangeParticular
              }] : []}
            />
          </div>
        </header>

        <div className="dashboard-content particular-content">
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
            />
          )}
          {isRefreshing && (
            <div className="particular-refresh-indicator">
              <i className="fas fa-spinner fa-spin" />
              <span>Actualizando datos...</span>
            </div>
          )}
          {renderContent()}
        </div>
      </main>
      {renderModal()}
      <SubscriptionBlockedModal
        open={subscriptionBlockedOpen}
        message={subscriptionBlockedMessage || undefined}
        onClose={() => setSubscriptionBlockedOpen(false)}
      />
      {aulaResourcesModal.open && (
        <AulaRecursosModal
          open={aulaResourcesModal.open}
          aula={aulaResourcesModal.aula}
          hasMensajes={hasMensajesAula}
          hasArchivos={hasArchivosAula}
          hasAsistentes={hasAgentesClasicos}
          hasLinks={hasLinksAula}
          globalAvailability={{
            mostrarArchivos: hasArchivosAula,
            mostrarLinks: hasLinksAula,
            mostrarPizarra: hasPizarraAula,
            mostrarChat: hasChatAula,
            mostrarVideollamada: hasVideollamadaAula,
            mostrarForo: hasMensajesAula,
            mostrarAsistentesIA: hasAgentesClasicos || hasOpenAIChats
          }}
          mensajesService={particularMensajesService}
          archivosService={particularArchivosService}
          linksService={particularLinksService}
          asistentesService={particularAsistentesService}
          resourceVisibilityService={(id, payload) => particularesApiService.updateAulaRecursosVisibilidad(id, payload)}
          onAulaUpdated={handleAulaUpdated}
          canUploadFiles
          onClose={() => setAulaResourcesModal({ open: false, aula: null })}
        />
      )}
      {aulaStatsModal.open && (
        <AulaStatsModal
          open={aulaStatsModal.open}
          aula={aulaStatsModal.aula}
          statsService={loadAulaStats}
          hubPath="/hubs/particular-aula-stats"
          title="Estadísticas del aula"
          onClose={() => setAulaStatsModal({ open: false, aula: null })}
        />
      )}
    </div>
  );
};

export default ParticularDashboard;
